import { inject, injectable } from "inversify";
import { action, computed, configure, makeObservable, observable, when } from "mobx";
import { ListChatsAttendingUseCase } from "@dropDesk/domain/use_case/chat/list_chats.usecase";
import { ListChatsWaitingChatUseCase } from "@dropDesk/domain/use_case/chat/list_waiting_chat.usecase";
import { Subscription } from "rxjs";
import { ListPaginationEntity } from "@dropDesk/domain/entities/common/list_pagination.entity";
import { TicketEntity } from "@dropDesk/domain/entities/ticket/ticket.entity";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import { translate } from "@dropDesk/storage/i18n/translate_helper";
import { SubscribeChatsUseCase } from "@dropDesk/domain/use_case/chat/subscribe_chats.usecase";
import { RoutesEnum } from "@dropDesk/domain/entities/routes/routes_enum";
import { BodyMessage, TicketMessageEntity } from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";
import { ConstantsKeys } from "@dropDesk/domain/entities/constants/constants_keys";
import { ListChatMessagesUseCase } from "@dropDesk/domain/use_case/chat/list_chat_messages.usecase";
import { ListChatMessagesV2UseCase } from "@dropDesk/domain/use_case/chat/list_chat_messages_v2.usecase";
import {
    ListChatMessagesAfterLastSubsUseCase
} from "@dropDesk/domain/use_case/chat/list_chat_messages_after_last_subs.usecase";
import { StartConnectionChatsUseCase } from "@dropDesk/domain/use_case/chat/start_connection_chats.usecase";
import { ChatIntegrationConnectionEntity } from "@dropDesk/domain/entities/chat/chat_integration_connection.entity";
import {
    findMaxNumber,
    findMinNumber,
    getIdCompanyByLocalStorage,
    initializeConfigurationLocalStorage,
    sleep
} from "@dropDesk/utils/helpers/common";
import { StatusTypes, TabsChat } from "@dropDesk/domain/entities/chat/chat_enum";
import { LogoutChatsUseCase } from "@dropDesk/domain/use_case/chat/logout_chats.usecase";
import { ConnectionStateChatsUseCase } from "@dropDesk/domain/use_case/chat/connection_state_chats.usecase";
import { SubscribeChatConnectionUseCase } from "@dropDesk/domain/use_case/chat/subscribe_chat_connection.usecase";
import { Howl } from 'howler';
import soundNotificationWaitingList from "@dropDesk/notification_waiting_old.mp3"
import soundNotificationMessage from "@dropDesk/notification_new_message.mp3"
import { UserEntity } from "@dropDesk/domain/entities/user/user.entity";
import {
    AudioMessageEntity,
    convertFileToMessageEntity,
    getMessageTypeFromFile,
    MessageChatType,
    MessageStatus,
    ReactionMessageEntity,
    ReplyEntity
} from "@dropDesk/domain/entities/ticket/message/external/message_entity";
import { NavigateFunction } from "react-router-dom";
import { SendMessageUseCase } from "@dropDesk/domain/use_case/chat/send_message_message.usecase";
import { ResendMessageUseCase } from "@dropDesk/domain/use_case/chat/resend_message.usecase";
import { DeleteMessageUseCase } from "@dropDesk/domain/use_case/chat/delete_message.usecase";
import { SubscriptionUploadFileUseCase } from "@dropDesk/domain/use_case/chat/upload_file_message.usecase";
import { EditMessageUseCase } from "@dropDesk/domain/use_case/chat/edit_message.usecase";
import { FileMessageEntity } from "@dropDesk/domain/entities/common/file_message";
import { generateUUIDV4 } from "@dropDesk/utils/uuidv4/uuidv4";
import { UploadOptionsEntity } from "@dropDesk/domain/entities/ticket/message/upload_message.entity";
import { AudioRecordEntity } from "@dropDesk/domain/entities/common/audio_record.entity";
import { downloadFile, fileToBase64 } from "@dropDesk/utils/helpers/files";
import firebase from "firebase/compat";
import { NavigateToMessageUseCase } from "@dropDesk/domain/use_case/chat/navigate_to_message.usecase";
import { NavigateToMessageV2UseCase } from "@dropDesk/domain/use_case/chat/navigate_to_message_v2.usecase";
import { newMessage } from "@dropDesk/presentation/pages/chat/controller/new_message";
import { ReactionMessageUseCase } from "@dropDesk/domain/use_case/chat/reaction_message.usecase";
import { InformReadMessageUseCase } from "@dropDesk/domain/use_case/chat/inform_read_message.usecase";
import { UrlToBlobUseCase } from "@dropDesk/domain/use_case/io/url_to_blob.usecase";
import { ClearTicketsCacheUseCase } from "@dropDesk/domain/use_case/chat/clear_tickets_cache.usecase";
import { CancelTokenSource } from "axios";
import { displayDateToLocale, getFormattedDate, getNowSumIndex } from "@dropDesk/utils/helpers/date_helper";
import { LeaveTicketChatUseCase } from "@dropDesk/domain/use_case/chat/leave_ticket.usecase";
import { AllChatsEntity } from "@dropDesk/domain/entities/chat/all_chats.entity";
import { ListAllChatsChatUseCase } from "@dropDesk/domain/use_case/chat/list_all_chat.usecase";
import { removeDiacritics } from "@dropDesk/utils/helpers/string_helper";
import {
    UpdateCacheFromSingleTicketUseCase
} from "@dropDesk/domain/use_case/chat/update_cache_from_single_ticket_message.usecase";
import { DropDeskErrorEnum } from "@dropDesk/domain/entities/exceptions/dropdesk_error_enum";
import { GetTicketFromCacheUseCase } from "@dropDesk/domain/use_case/chat/get_ticket_from_cache.usecase";
import { ExportTicketStatusEntity } from "@dropDesk/domain/entities/ticket/export_ticket_status.entity";
import { ExportHTMLTicketsUseCase } from "@dropDesk/domain/use_case/ticket/export_pdf_tickets.usecase";
import { DeleteExportHTMLTicketsUseCase } from "@dropDesk/domain/use_case/ticket/delete_export_pdf_tickets.usecase";
import {
    GetCurrentExportStateTicketsUseCase
} from "@dropDesk/domain/use_case/ticket/get_current_export_pdf_tickets.usecase";
import { BaseMessageEntity } from "@dropDesk/domain/entities/messages/base_message_entity";
import { GetUsersFromCacheUseCase } from "@dropDesk/domain/use_case/chat/get_users_from_cache.usecase";
import { GetMessageByIdUseCase } from "@dropDesk/domain/use_case/chat/get_message_by_id.usecase";
import FirebaseStorageError = firebase.storage.FirebaseStorageError;
import { ChatMessageEntity, StatusMessageError } from "@dropDesk/domain/entities/messages/message_enums";
import { DropDeskException, ServerError } from "@dropDesk/domain/entities/exceptions/exceptions";
import { FeatureToggle } from "@dropDesk/data/clients/firebase_remote_configs_client";
import { ListPaginationChatMessagesEntity } from "@dropDesk/domain/entities/chat/list_pagination_chat_messages.entity";
import {
    ChatMessagesV2SessionEntity,
    ChatMessagesV2StateEntity,
    ChatMessagesV2TicketCacheEntity
} from "@dropDesk/domain/entities/chat/chat_messages_v2_session.entity";

export enum TypeListChat { waiting = 'waiting', attending = 'attending' }

export enum ActiveRouteEnum {
    ticket = 'ticket',
    chat = 'chat'
}

type ChatMessagesV2CursorSide = 'older' | 'newer' | 'both';

configure({
    enforceActions: "always",
});

@injectable()
export class ChatController {

    private readonly _listChatsAttendingUseCase: ListChatsAttendingUseCase;
    private readonly _listChatsWaitingChatUseCase: ListChatsWaitingChatUseCase;
    private readonly _subscribeChatsUseCase: SubscribeChatsUseCase;
    private readonly _listChatMessagesUseCase: ListChatMessagesUseCase;
    private readonly _listChatMessagesV2UseCase: ListChatMessagesV2UseCase;
    private readonly _listChatMessagesAfterLastSubsUseCase: ListChatMessagesAfterLastSubsUseCase;
    private readonly _startConnectionChatsUseCase: StartConnectionChatsUseCase;
    private readonly _logoutChatsUseCase: LogoutChatsUseCase;
    private readonly _connectionStateChatsUseCase: ConnectionStateChatsUseCase;
    private readonly _subscribeChatConnectionUseCase: SubscribeChatConnectionUseCase;
    private readonly _sendMessageUseCase: SendMessageUseCase;
    private readonly _resendMessageUseCase: ResendMessageUseCase;
    private readonly _deleteMessageUseCase: DeleteMessageUseCase;
    private readonly _subscriptionUploadFileUseCase: SubscriptionUploadFileUseCase;
    private readonly _editMessageUseCase: EditMessageUseCase;
    private readonly _navigateToMessageUseCase: NavigateToMessageUseCase;
    private readonly _navigateToMessageV2UseCase: NavigateToMessageV2UseCase;
    private readonly _reactionMessageUseCase: ReactionMessageUseCase;
    private readonly _informReadMessageUseCase: InformReadMessageUseCase;
    private readonly _urlToBlobUseCase: UrlToBlobUseCase;
    private readonly _clearTicketsCacheUseCase: ClearTicketsCacheUseCase;
    private readonly _leaveTicketChatUseCase: LeaveTicketChatUseCase;
    private readonly _getTicketFromCacheUseCase: GetTicketFromCacheUseCase;
    private readonly _listAllChatsChatUseCase: ListAllChatsChatUseCase;
    private readonly _updateCacheFromSingleTicketUseCase: UpdateCacheFromSingleTicketUseCase;
    private readonly _exportHTMLTicketsUseCase: ExportHTMLTicketsUseCase;
    private readonly _deleteExportHTMLTicketsUseCase: DeleteExportHTMLTicketsUseCase;
    private readonly _getCurrentExportStateTicketsUseCase: GetCurrentExportStateTicketsUseCase;
    private readonly _getUsersFromCacheUseCase: GetUsersFromCacheUseCase;
    private readonly _getMessageByIdUseCase: GetMessageByIdUseCase;


    constructor(
        @inject(ListChatsAttendingUseCase) listChatsAttendingUseCase: ListChatsAttendingUseCase,
        @inject(ListChatsWaitingChatUseCase) listChatsWaitingChatUseCase: ListChatsWaitingChatUseCase,
        @inject(SubscribeChatsUseCase) subscribeChatsUseCase: SubscribeChatsUseCase,
        @inject(ListChatMessagesUseCase) listChatMessagesUseCase: ListChatMessagesUseCase,
        @inject(ListChatMessagesV2UseCase) listChatMessagesV2UseCase: ListChatMessagesV2UseCase,
        @inject(ListChatMessagesAfterLastSubsUseCase) listChatMessagesAfterLastSubsUseCase: ListChatMessagesAfterLastSubsUseCase,
        @inject(StartConnectionChatsUseCase) startConnectionChatsUseCase: StartConnectionChatsUseCase,
        @inject(ConnectionStateChatsUseCase) connectionStateChatsUseCase: ConnectionStateChatsUseCase,
        @inject(LogoutChatsUseCase) logoutChatsUseCase: LogoutChatsUseCase,
        @inject(SubscribeChatConnectionUseCase) subscribeChatConnectionUseCase: SubscribeChatConnectionUseCase,
        @inject(SendMessageUseCase) sendMessageUseCase: SendMessageUseCase,
        @inject(ResendMessageUseCase) resendMessageUseCase: ResendMessageUseCase,
        @inject(DeleteMessageUseCase) deleteMessageUseCase: DeleteMessageUseCase,
        @inject(SubscriptionUploadFileUseCase) subscriptionUploadFileUseCase: SubscriptionUploadFileUseCase,
        @inject(EditMessageUseCase) editMessageUseCase: EditMessageUseCase,
        @inject(NavigateToMessageUseCase) navigateToMessageUseCase: NavigateToMessageUseCase,
        @inject(NavigateToMessageV2UseCase) navigateToMessageV2UseCase: NavigateToMessageV2UseCase,
        @inject(ReactionMessageUseCase) reactionMessageUseCase: ReactionMessageUseCase,
        @inject(InformReadMessageUseCase) informReadMessageUseCase: InformReadMessageUseCase,
        @inject(UrlToBlobUseCase) urlToBlobUseCase: UrlToBlobUseCase,
        @inject(ClearTicketsCacheUseCase) clearTicketsCacheUseCase: ClearTicketsCacheUseCase,
        @inject(LeaveTicketChatUseCase) leaveTicketChatUseCase: LeaveTicketChatUseCase,
        @inject(GetTicketFromCacheUseCase) getTicketFromCacheUseCase: GetTicketFromCacheUseCase,
        @inject(ListAllChatsChatUseCase) listAllChatsChatUseCase: ListAllChatsChatUseCase,
        @inject(UpdateCacheFromSingleTicketUseCase) updateCacheFromSingleTicketUseCase: UpdateCacheFromSingleTicketUseCase,
        @inject(ExportHTMLTicketsUseCase) exportHTMLTicketsUseCase: ExportHTMLTicketsUseCase,
        @inject(DeleteExportHTMLTicketsUseCase) deleteExportHTMLTicketsUseCase: DeleteExportHTMLTicketsUseCase,
        @inject(GetCurrentExportStateTicketsUseCase) getCurrentExportStateTicketsUseCase: GetCurrentExportStateTicketsUseCase,
        @inject(GetUsersFromCacheUseCase) getUsersFromCacheUseCase: GetUsersFromCacheUseCase,
        @inject(GetMessageByIdUseCase) getMessageByIdUseCase: GetMessageByIdUseCase,
    ) {
        makeObservable(this);
        this._listChatsAttendingUseCase = listChatsAttendingUseCase;
        this._listChatsWaitingChatUseCase = listChatsWaitingChatUseCase;
        this._subscribeChatsUseCase = subscribeChatsUseCase;
        this._listChatMessagesUseCase = listChatMessagesUseCase;
        this._listChatMessagesV2UseCase = listChatMessagesV2UseCase;
        this._listChatMessagesAfterLastSubsUseCase = listChatMessagesAfterLastSubsUseCase;
        this._startConnectionChatsUseCase = startConnectionChatsUseCase;
        this._connectionStateChatsUseCase = connectionStateChatsUseCase;
        this._logoutChatsUseCase = logoutChatsUseCase;
        this._subscribeChatConnectionUseCase = subscribeChatConnectionUseCase;
        this._sendMessageUseCase = sendMessageUseCase;
        this._resendMessageUseCase = resendMessageUseCase;
        this._deleteMessageUseCase = deleteMessageUseCase;
        this._subscriptionUploadFileUseCase = subscriptionUploadFileUseCase;
        this._editMessageUseCase = editMessageUseCase;
        this._navigateToMessageUseCase = navigateToMessageUseCase;
        this._navigateToMessageV2UseCase = navigateToMessageV2UseCase;
        this._reactionMessageUseCase = reactionMessageUseCase;
        this._informReadMessageUseCase = informReadMessageUseCase;
        this._urlToBlobUseCase = urlToBlobUseCase;
        this._clearTicketsCacheUseCase = clearTicketsCacheUseCase;
        this._leaveTicketChatUseCase = leaveTicketChatUseCase;
        this._getTicketFromCacheUseCase = getTicketFromCacheUseCase;
        this._listAllChatsChatUseCase = listAllChatsChatUseCase;
        this._updateCacheFromSingleTicketUseCase = updateCacheFromSingleTicketUseCase;
        this._exportHTMLTicketsUseCase = exportHTMLTicketsUseCase;
        this._deleteExportHTMLTicketsUseCase = deleteExportHTMLTicketsUseCase;
        this._getCurrentExportStateTicketsUseCase = getCurrentExportStateTicketsUseCase;
        this._getUsersFromCacheUseCase = getUsersFromCacheUseCase;
        this._getMessageByIdUseCase = getMessageByIdUseCase;
    }

    connection = new ChatIntegrationConnectionEntity({
        idCompany: getIdCompanyByLocalStorage(),
        qrCode: null,
        status: StatusTypes.DISCONNECTED
    });

    @observable
    tableListWaiting: ListPaginationEntity<TicketEntity> = ListPaginationEntity.defaultValue<TicketEntity>();

    @observable
    tableListChats: ListPaginationEntity<TicketEntity> = ListPaginationEntity.defaultValue<TicketEntity>();

    @observable
    chatConnection: ChatIntegrationConnectionEntity = this.connection;

    @observable
    currentChat?: TicketEntity | null;

    @observable
    compactModeMessages: boolean = localStorage.getItem(ConstantsKeys.compactModeMessages)
        ?
        JSON.parse(localStorage.getItem(ConstantsKeys.compactModeMessages)!)
        :
        false;

    @observable
    isSearchByMyChats: boolean = true;

    @observable
    forceLogout: boolean = false;

    @observable
    activeTabChat: TabsChat = TabsChat.attending;

    @observable
    contactTicketId: string | null = null;

    @observable
    idMessageFocused: string | null = null;

    @observable
    forceCleanMessagesCache: boolean = false;

    @observable
    exportTicketStatus: ExportTicketStatusEntity | null = null;

    @observable
    currentChatMessages: ListPaginationEntity<TicketMessageEntity> = ListPaginationEntity.defaultValue<TicketMessageEntity>();

    @observable
    soundNewMessageChat: boolean = localStorage.getItem(ConstantsKeys.soundNewMessageChat)
        ?
        JSON.parse(localStorage.getItem(ConstantsKeys.soundNewMessageChat)!)
        :
        true;

    @observable
    soundQueueWaitingChat: boolean = localStorage.getItem(ConstantsKeys.soundQueueWaitingChat)
        ?
        JSON.parse(localStorage.getItem(ConstantsKeys.soundQueueWaitingChat)!)
        :
        false;

    @observable
    cacheChat: Record<string, Record<number, TicketMessageEntity[]>> = {};

    @observable
    chatMessagesV2: ChatMessagesV2StateEntity = ChatMessagesV2StateEntity.empty();

    @observable
    scrollPosition: 'top' | 'bottom' | null = null;

    @observable
    notificationWaiting: Howl | null = null;

    @observable
    notificationNewMessage: Howl | null = null;

    @observable
    readByUsers: UserEntity[] = [];

    @observable
    receivedByUsers: UserEntity[] = [];

    @computed
    get filteredListWaiting(): TicketEntity[] {
        if (this.searchParam !== '' && !this.isSearchByMyChats) {
            return this.tableListWaiting.data.filter((ticket) => this.filterPredicate(ticket));
        }
        return this.tableListWaiting.data;
    }

    @computed
    get filteredListChat(): TicketEntity[] {
        if (this.searchParam !== '' && this.isSearchByMyChats) {
            return this.tableListChats.data.filter((ticket) => this.filterPredicate(ticket));
        }

        return this.tableListChats.data;
    }

    @computed
    get countNotificationDocumentHead(): number {
        return (
            (this.tableListWaiting.totalRows) +
            (this.tableListChats.data.filter((entry) => !!entry.countUnreadNotification)).length
        )
    }

    @computed
    get countNotificationAttending(): number {
        return this.tableListChats.data.filter((entry) => !!entry.countUnreadNotification).length
    }


    @computed
    get messages(): TicketMessageEntity[] {
        if (this.forceCleanMessagesCache) {
            return [];
        }
        return this.orderMessagesByCreatedAt(
            this.makeStartingAndEndTime(
                this.removeDuplicateMessages(
                    this.currentChatMessages.data.filter((entry) => !this.currentChat?.hasActiveStatus ? !entry.reactionMessage && !entry.isSystemMessage : !entry.reactionMessage))
            )
        );
    }

    @computed
    get reactions(): TicketMessageEntity[] {
        return this.removeDuplicateMessages(
            this.currentChatMessages.data.filter((entry) => !!entry.reactionMessage)
        );

    }

    @computed
    get realExportation(): ExportTicketStatusEntity | null {
        if (this.currentChat?.id === this.exportTicketStatus?.idTicket) {
            return this.exportTicketStatus;
        }
        return null;
    }

    @computed
    get targetMessagePaginationTop(): TicketMessageEntity | undefined {
        if (FeatureToggle.isChatMessagesV2Enabled()) {
            if (
                this.forceCleanMessagesCache
                ||
                this.loading
                ||
                this.targetVirtualizedMessage
                ||
                this.messages.length === 0
            ) {
                return;
            }
            if (
                !this.chatMessagesV2.listingHasMore
                &&
                !this.canAutoLoadPreviousOnce()
            ) {
                return;
            }
            return this.messages[this.messages.length - 1];
        }
        if (
            this.forceCleanMessagesCache
            ||
            this.loading
            ||
            this.targetVirtualizedMessage
        ) {
            return;
        }
        return this.messages[this.messages.length - 10];
    }

    @computed
    get targetMessageButtonNavigateBottom(): TicketMessageEntity | undefined {
        if (this.forceCleanMessagesCache || this.messages.length === 0) {
            return;
        }
        return this.messages[0];
    }

    @computed
    get isAtLiveEndV2(): boolean {
        const openedId = this.currentChat?.id;
        if (!openedId) {
            return true;
        }
        const v2 = this.chatMessagesV2;
        return (
            v2.listingTicketId === openedId &&
            v2.listingPage === 0 &&
            v2.listingNewerTicketId === openedId &&
            v2.listingNewerPage === 0 &&
            !v2.listingHasNewer
        );
    }

    @computed
    get shouldForceShowNavigateToBottomV2(): boolean {
        if (this.isAtLiveEndV2) {
            return false;
        }
        const openedId = this.currentChat?.id;
        if (!openedId) {
            return false;
        }
        const livePage = this.chatMessagesV2.sessions[openedId]?.tickets[openedId]?.pages?.[0] ?? [];
        return livePage.length > 0;
    }

    @computed
    get targetMessagePaginationBottom(): TicketMessageEntity | undefined {
        if (this.forceCleanMessagesCache || this.loading || this.targetVirtualizedMessage) {
            return;
        }

        if (this.canAutoScrollNewerV2()) {
            return this.messages[this.messages.length > 10 ? 10 : this.messages.length - 1];
        }
        return;
    }

    @action
    addPagesLoad(value: number) {
        const pagesLoad = this.currentChatMessages.pagesLoad;
        return pagesLoad?.includes(value) ? pagesLoad : [...pagesLoad ?? [], value];
    }

    @observable
    targetVirtualizedMessage: TicketMessageEntity | null = null;

    @observable
    subscriptionChats?: Subscription;

    @observable
    subscriptionConnection?: Subscription;

    @observable
    loadingGetCurrentStateExport = false;

    @observable
    searchParam = '';

    @observable
    loading = false;

    @observable
    loadingMessage?: string | null = null;

    @observable
    subscriptionUploadFile?: Subscription;

    @observable
    lastUsedReactions: string[] =
        (
            (localStorage.getItem(ConstantsKeys.lastUsedReactions as string) as string[] | null)
                ?
                JSON.parse(localStorage.getItem(ConstantsKeys.lastUsedReactions as string)!)
                :
                ['👍', '❤️', '😂', '😮', '😢', '🙏']
        );


    user?: UserEntity;

    activeRoute?: ActiveRouteEnum;

    navigate?: NavigateFunction;

    @action
    setForceCleanMessagesCache(value: boolean) {
        this.forceCleanMessagesCache = value;
    }

    @action
    setExportTicketStatus(value: ExportTicketStatusEntity | null) {
        this.exportTicketStatus = value;
    }

    @action
    setActiveTabChat(value: TabsChat) {
        this.activeTabChat = value;
    }

    @action
    setContactTicketId(id: string | null): void {
        this.contactTicketId = id;
    }

    @action
    setIdMessageFocused(id: string | null) {
        this.idMessageFocused = id;
    }

    @action
    setScrollPosition(value: 'top' | 'bottom' | null) {
        this.scrollPosition = value;
    }

    @action
    filterPredicate(ticket: TicketEntity) {
        const searchParam = removeDiacritics(this.searchParam).toLowerCase();

        const userMatch = ticket.usersInfo!.some((user: any) =>
            removeDiacritics(user.name.toLowerCase()).includes(removeDiacritics(searchParam))
            ||
            user.telephone?.toLowerCase().includes(searchParam)
        );

        const sectorMatch = removeDiacritics(ticket.sector?.name.toLowerCase() ?? '').includes(searchParam);

        return userMatch || sectorMatch;
    }

    @action
    setTableListWaiting(value: ListPaginationEntity<TicketEntity>) {
        this.tableListWaiting = value;
    }

    @action
    setSoundQueueWaitChat(value: boolean) {
        this.soundQueueWaitingChat = value;
        localStorage.setItem(ConstantsKeys.soundQueueWaitingChat, value.toString());
    }

    @action
    setSoundNewMessageChat(value: boolean) {
        this.soundNewMessageChat = value;
        localStorage.setItem(ConstantsKeys.soundNewMessageChat, value.toString());
    }

    @action
    setCompactModeMessage(value: boolean) {
        this.compactModeMessages = value;
        localStorage.setItem(ConstantsKeys.compactModeMessages, value.toString());
    }

    @action
    setIsSearchByMyChats(value: boolean) {
        this.isSearchByMyChats = value;
    }

    @action
    setChatConnection(value: ChatIntegrationConnectionEntity) {
        this.chatConnection = value;
    }

    orderTicketsByUpdateAt(tickets: TicketEntity[]) {
        tickets.sort((a, b) => new Date(b.updatedAt!).getTime() - new Date(a.updatedAt!).getTime());
    }

    orderTicketsByCreatedAt(tickets: TicketEntity[]) {
        tickets.sort((a, b) => new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime());
    }

    orderMessagesByCreatedAt(messages: TicketMessageEntity[]) {
        return BaseMessageEntity.orderMessagesByCreatedAt(messages);
    }

    @action
    setTableListChats(value: ListPaginationEntity<TicketEntity>) {
        this.tableListChats = value;
    }

    pushWaitingChatData(value: ListPaginationEntity<TicketEntity>) {
        const data = this.getUniqueList(this.tableListWaiting.data, value.data);
        this.orderTicketsByCreatedAt(data);
        this.setTableListWaiting(this.tableListWaiting.copyWith({
            ...value,
            data
        }));
    }

    pushAttendingChatData(value: ListPaginationEntity<TicketEntity>) {
        const data = this.getUniqueList(this.tableListChats.data, value.data);
        this.orderTicketsByUpdateAt(data);
        this.setTableListChats(this.tableListChats.copyWith({
            ...value,
            data
        }));
    }

    @action
    setSearchParam(value: string) {
        this.searchParam = value;
    }

    @action
    setForceLogout(force: boolean) {
        this.forceLogout = force;
    }

    @action
    resetTable() {
        if (this.isSearchByMyChats) {
            this.setTableListChats(ListPaginationEntity.defaultValue<TicketEntity>());
        } else {
            this.setTableListWaiting(ListPaginationEntity.defaultValue<TicketEntity>())
        }
    }

    @action
    startLoading(loadingMessage?: string | null) {
        this.setLoadingMessage(loadingMessage);
        this.loading = true;
    }

    @action
    stopLoading() {
        this.loading = false;
        this.setLoadingMessage(null);
    }

    @action
    setLoadingGetCurrentStateExport(value: boolean) {
        this.loadingGetCurrentStateExport = value;
    }

    @action
    setLoadingMessage(message?: string | null) {
        this.loadingMessage = message;
    }

    @action
    eligibleMessagesToRead(): TicketMessageEntity[] {
        if (!this.currentChat) {
            return [];
        }
        return this.currentChatMessages.data.filter((entry) => entry.idTicket === this.currentChat!.id)
    }

    subscribeConnection = (
        onSuccess: (key: string) => void,
        onDuplicated: () => void,
        onExpired: (key: string) => void,
        id: string,
        useSocket: boolean,
        handleDisconnect: (temporary: boolean) => void,
        getLastUpdates: () => void
    ): void => {
        this.subscriptionConnection?.unsubscribe();
        this.subscriptionConnection = this._subscribeChatConnectionUseCase.call(id, useSocket, handleDisconnect, getLastUpdates).subscribe({
            next: (connection: ChatIntegrationConnectionEntity | null) => {
                if (connection) {

                    this.setChatConnection(connection);
                    if (connection.duplicated) {
                        onDuplicated();
                    }
                    if (connection.hasActiveConnection) {
                        this.dispose();
                        onSuccess("chat.success.connection");
                    }
                    if (connection.hasInactiveConnection) {
                        onExpired("chat.expiredConnect");
                    }
                }
            },
            error: (err: any) => {
                toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
                this.stopLoading();
                this.subscriptionConnection?.unsubscribe();
            }
        });
    }
    @action
    startConnection = async (
        onSuccess: (key: string) => void,
        onDuplicated: () => void,
        onExpired: (key: string) => void,
        id: string,
        useSocket: boolean,
        handleDisconnect: (temporary: boolean) => void,
        getLastUpdates: () => void
    ): Promise<void> => {
        try {
            this.startLoading("chat.initConnection");
            this.subscribeConnection(
                onSuccess,
                onDuplicated,
                onExpired,
                id,
                useSocket,
                handleDisconnect,
                getLastUpdates,
            );
            const connection = await this._startConnectionChatsUseCase.call();
            this.setChatConnection(this.chatConnection.copyWith({
                ...connection
            }));
            if (connection.hasActiveConnection) {
                this.dispose();
                onSuccess("chat.success.connection");
            }
            if (connection.duplicated) {
                onDuplicated();
            }
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    findPageById(messageId: string) {
        const data = this.cacheChat[this.currentChat?.id ?? 'unknown'];
        for (const page in data) {
            if (data.hasOwnProperty(page)) {
                const objectsOnPage = data[page];
                for (const object of objectsOnPage) {
                    if (object.id === messageId || object.idExternal === messageId) {
                        return parseInt(page);
                    }
                }
            }
        }
        return null;
    }


    @action
    handleMoveCursorToFinal = () => {
        if (FeatureToggle.isChatMessagesV2Enabled()) {
            this.handleMoveCursorToFinalV2();
            return;
        }
        const messages = this.getMessagesCacheFromPage(0);
        this.updateMessage(
            this.currentChat!,
            messages,
            false,
            true,
            this.currentChatMessages.copyWith({
                page: 0,
            }),
            false,
            true,
        );
        this.handleVirtualizedMessages();
    }

    @action
    handleResetMessageFocused = () => {
        this.setIdMessageFocused(null);
    }

    @action
    logout = async (onSuccess: (key: string) => void): Promise<void> => {
        try {
            this.startLoading("chat.logout");
            await this._logoutChatsUseCase.call(this.forceLogout);
            this.setChatConnection(this.chatConnection.copyWith({
                qrCode: null,
                status: StatusTypes.DISCONNECTED
            }));
            onSuccess("chat.success.logout");
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    leaveTicket(): void {
        if (this.currentChat && this.currentChat.id) {
            this._leaveTicketChatUseCase.call(this.currentChat!.id);
            this.dispose();
        }
    }

    @action
    connectionState = async (): Promise<void> => {
        try {
            this.startLoading("chat.connectionState");
            const response = await this._connectionStateChatsUseCase.call();
            this.setChatConnection(response);
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }
    @action
    listChatsAttending = async (searchParam: string): Promise<void> => {
        try {
            this.startLoading();
            this.setIsSearchByMyChats(true);
            this.eraseChat();
            if (searchParam !== this.searchParam) {
                this.resetTable();
            }
            this.setSearchParam(searchParam ?? '');
            const response = await this._listChatsAttendingUseCase.call(searchParam, this.tableListChats.page);
            this.setTableListChats(response);
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    };

    @action
    listChatsWaiting = async (searchParam: string): Promise<void> => {
        try {
            this.startLoading();
            this.setIsSearchByMyChats(false);
            this.eraseChat();
            if (searchParam !== this.searchParam) {
                this.resetTable();
            }
            this.setSearchParam(searchParam ?? '');
            const response = await this._listChatsWaitingChatUseCase.call(searchParam, this.tableListWaiting.page);
            this.setTableListWaiting(response);
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    };

    @action
    listAllChats = async (searchParam: string, cancelTokenSource?: CancelTokenSource): Promise<void> => {
        return new Promise<void>(async (resolve, reject) => {
            try {
                this.startLoading("ticket.loadingChatInit");
                this.setSearchParam(searchParam ?? '');
                const response: AllChatsEntity = await this._listAllChatsChatUseCase.call(searchParam, cancelTokenSource);
                this.setTableListChats(response.attending);
                this.setTableListWaiting(response.waiting);
                this.stopLoading();
                return resolve();
            } catch (err: any) {
                if (err.message !== DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                    toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
                }
                this.stopLoading();
                return reject(err);
            }
        });
    };
    @action
    getDataFromPageChatsWaiting = async (): Promise<void> => {
        if (this.tableListWaiting.hasMore) {
            this.startLoading();
            const newPage = this.tableListWaiting.page + 1;
            const response = await this._listChatsWaitingChatUseCase.call(this.searchParam, newPage);
            this.pushWaitingChatData(response);
            this.stopLoading();
        }
    }

    @action
    getTicketFromCache = (id: string): TicketEntity | undefined => {
        return this._getTicketFromCacheUseCase.call(id);
    }
    @action
    getDataFromPageChatsAttending = async (): Promise<void> => {
        if (this.tableListChats.hasMore) {
            this.startLoading();
            const newPage = this.tableListChats.page + 1;
            const response = await this._listChatsAttendingUseCase.call(this.searchParam, newPage);
            this.pushAttendingChatData(response);
            this.stopLoading();
        }
    }

    @action
    private async listChatMessagesAfterLastSubscription(data: TicketEntity[], oldCurrentChat?: TicketEntity | null): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                const currentChat = this.currentChat;
                const canFetchMessages: boolean =
                    !!this.getTargetTicket(data)
                    && (currentChat?.updatedAt !== oldCurrentChat?.updatedAt || (!!oldCurrentChat?.updateOp && currentChat?.updateOp !== oldCurrentChat?.updateOp));
                if (canFetchMessages && currentChat) {
                    const response: TicketMessageEntity[] = await this._listChatMessagesAfterLastSubsUseCase.call(currentChat.id);
                    this.updateMessage(currentChat, response, true, true);
                    this.informReadMessagesIfNeeded(response).then();
                }
                return resolve();
            } catch (err: any) {
                return reject(err);
            }
        })
    }

    @action
    async informReadMessagesIfNeeded(messages?: TicketMessageEntity[], cancelTokenSource?: CancelTokenSource) {
        if (messages && messages.length > 0) {
            const isNecessaryReadMessages =
                messages.filter((entry) => entry.senderId !== this.user!.id).length > 0
                && messages.filter((entry) => !!entry.readers && !entry.readers[this.user!.id]).length > 0;
            if (isNecessaryReadMessages) {
                this.informReadMessages(cancelTokenSource).then();
            }
        }
    }

    @action
    async informReadMessages(cancelTokenSource?: CancelTokenSource) {
        try {
            const currentChat = this.currentChat;
            if (currentChat) {
                this.resetCountNotification(currentChat.id);
                await this._informReadMessageUseCase.call(currentChat.id, cancelTokenSource);
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    private resetCountNotification(ticketId: string): void {
        let index: number | undefined = this.tableListChats.data.findIndex(item => ticketId == item.id);
        let isTableListChats = true;
        if (index === -1) {
            isTableListChats = false;
            index = this.tableListWaiting.data.findIndex(item => ticketId == item.id);
        }
        if (index > -1) {
            if (isTableListChats) {
                this.tableListChats.data[index] = this.tableListChats.data[index].copyWith({
                    countUnreadNotification: 0
                });
            } else {
                this.tableListWaiting.data[index] = this.tableListWaiting.data[index].copyWith({
                    countUnreadNotification: 0
                });
            }
        }
    }

    @action
    private notificationAudioNewMessage(tickets: TicketEntity[]) {
        const list = tickets.filter((item) => this.user ? item.hasEligibleChatAttending(this.user) : false);
        for (const ticketEntity of list) {
            if (
                this.soundNewMessageChat &&
                //this.currentChat?.id !== ticketEntity.id &&
                ticketEntity.soundPlayed
            ) {
                this.notificationNewMessage?.play();
                ticketEntity.soundPlayed = false;
                this._updateCacheFromSingleTicketUseCase.call(ticketEntity);
            }
        }
    }

    @action
    playAudioWaitingListDepending(): void {
        if (this.filteredListWaiting.length > 0) {
            if (!this.notificationWaiting?.playing() && this.soundQueueWaitingChat) {
                this.playAudioWaitingList();
            }
            if (this.notificationWaiting?.playing() && !this.soundQueueWaitingChat) {
                this.stopNotificationWaiting();
            }
        } else {
            this.stopNotificationWaiting();
        }
    }

    @action
    playAudioWaitingList(): void {
        this.notificationWaiting?.play();
    }

    @action
    stopNotificationWaiting(): void {
        this.notificationWaiting?.stop();
    }

    @action
    resetSounds(): void {
        this.notificationWaiting = null;
        this.notificationNewMessage = null
    }

    private initChatDependant(tickets: TicketEntity[]) {
        for (const ticket of tickets) {
            if (
                ticket.isMeStartingChat
                // &&
                // (
                //     (!this.currentChat) ||
                //     (this.currentChat?.id === ticket.id && this.currentChat?.hasOpenStatus)
                // )
            ) {
                this.handleInitChat(ticket);
            }
        }
    }

    @action
    initChatDependantFromContact(ticket: TicketEntity) {
        if (ticket.attendant?.id === this.user?.id) {
            this.handleInitChat(ticket);
        }
    }

    private getUniqueList(originalList: TicketEntity[], newList: TicketEntity[]): TicketEntity[] {
        const result = originalList;

        if (newList.length) {
            for (const ticket of newList) {
                const index = originalList.findIndex((item) => item.id === ticket.id);
                if (index > -1) {
                    result[index] = ticket;
                } else {
                    result.push(ticket);
                }
            }
        }

        return result;
    }

    handleChangeListWaiting(data: TicketEntity[]): void {
        const waitingList = data.filter((item) => this.user ? item.hasEligibleChatWaiting(this.user) : item.hasOpenStatus);
        this.removeUnEligibleTable(waitingList, this.tableListWaiting);
        const result = this.getUniqueList(this.tableListWaiting.data, waitingList);
        this.orderTicketsByCreatedAt(result);
        this.setTableListWaiting(this.tableListWaiting.copyWith({
            data: result,
            totalRows: result.length,
        }));
    }

    handleChangeListAttending(data: TicketEntity[]): void {
        const attendingList = data.filter((item) => this.user ? item.hasEligibleChatAttending(this.user) : item.hasAttendingStatus);
        this.removeUnEligibleTable(attendingList, this.tableListChats);
        const result = this.getUniqueList(this.tableListChats.data, attendingList);
        this.orderTicketsByUpdateAt(result);
        this.setTableListChats(this.tableListChats.copyWith({
            data: result,
            totalRows: result.length,
        }));
    }

    @action
    handleInitChat(ticket: TicketEntity) {
        this.setActiveTabChat(TabsChat.attending);
        this.handleUpdateTicket(ticket);
        this.setChat(ticket, false);
    }

    @action
    handleUpdateTicket(ticket: TicketEntity) {
        //AQUI
        //ticket.updatedAt =  getFormattedDate();
        if (this.activeRoute === ActiveRouteEnum.chat) {
            const data = [...this.tableListWaiting.data, ...this.tableListChats.data];
            const index = data.findIndex((item) => item.id === ticket.id);
            if (index > -1) {
                data[index] = ticket;
            } else {
                data.push(ticket);
            }
            this.handleChangeListWaiting(data);
            this.handleChangeListAttending(data);
        }
        this.updateCacheDataSource(ticket);
    }

    public preventUnauthorizedAccessChat(tickets: TicketEntity[]) {
        if (!this.user) {
            return;
        }
        const ticketsToCheck = this.activeRoute === ActiveRouteEnum.ticket
            ? tickets.filter((ticket) => ticket.id === this.currentChat?.id)
            : tickets;
        for (const ticket of ticketsToCheck) {
            const canAccess = ticket.canAccess(this.user);
            if (!canAccess) {
                if (this.currentChat && this.currentChat.id === ticket.id) {
                    this.eraseChat();
                }
                if (this.activeRoute === ActiveRouteEnum.ticket && this.navigate) {
                    this.navigate(RoutesEnum.tickets);
                    toastMessage.warning('Você não tem permissão para acessar este atendimento ou ' +
                        'não faz parte do seu setor de atuação, por favor verifique.');
                }
            }
        }
    }

    private getUniqueCacheMessagesList(originalList: TicketMessageEntity[], newMessages: TicketMessageEntity[], isActionDelete = false): TicketMessageEntity[] {
        const result = originalList;

        if (newMessages.length) {
            for (const message of newMessages) {
                const index = originalList.findIndex((item) => item.id === message.id);
                if (index > -1) {
                    if (isActionDelete) {
                        result.splice(index, 1);
                    } else {
                        result[index] = result[index].getMessagePreventDefaultCache(message);
                    }
                } else {
                    result.push(message);
                }
            }
        }

        return result;
    }


    @action
    updateCacheDataSource(ticket: TicketEntity): void {
        this._updateCacheFromSingleTicketUseCase.call(ticket);
    }

    @action
    removeAllValues(): void {
        this.cacheChat = {};
        this.resetChatMessagesV2State();
        this._clearTicketsCacheUseCase.call();
        this.setCurrentChatMessages(ListPaginationEntity.defaultValue<TicketMessageEntity>());
        this.setTableListChats(ListPaginationEntity.defaultValue<TicketEntity>());
        this.setTableListWaiting(ListPaginationEntity.defaultValue<TicketEntity>());
        this.eraseChat();
        this.setContactTicketId(null);
        this.resetSounds();
    }

    @action
    setCacheChat(ticket: TicketEntity, messages: TicketMessageEntity[], page: number, isActionDelete = false): void {
        const cache = this.cacheChat[ticket.id] ?? {};
        const messagesFromPage = cache[page] ?? [];
        cache[page] = this.getUniqueCacheMessagesList(messagesFromPage, messages, isActionDelete);
        this.cacheChat[ticket.id] = cache;
    }

    @action
    exportHtml = async (
        onError: (exportation: ExportTicketStatusEntity) => void,
    ): Promise<void> => {
        try {
            this.setLoadingGetCurrentStateExport(true);
            const result = await this._exportHTMLTicketsUseCase.call(this.currentChat!.id);
            if (!result.success) {
                onError(result.data);
                this.setExportTicketStatus(null);
            } else {
                this.setExportTicketStatus(result.data);
            }
            this.setLoadingGetCurrentStateExport(false);
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.setLoadingGetCurrentStateExport(false);
        }
    }

    @action
    async getCurrentExportState(): Promise<void> {
        try {
            const result = await this._getCurrentExportStateTicketsUseCase.call();
            this.setExportTicketStatus(result);
            this.setLoadingGetCurrentStateExport(false);
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.setLoadingGetCurrentStateExport(false);
        }
    }

    @action
    async onClickDownloadExportTicket(exportStatus: ExportTicketStatusEntity) {
        const url = exportStatus.url;
        if (url) {
            toastMessage.info('Iniciando download do documento...');
            await downloadFile(url, `export_atendimento_${exportStatus.tickerNumber}_${displayDateToLocale(new Date().toISOString())}.html`, true);
            toastMessage.success('Download concluído com sucesso!');
        }
    }

    @action
    async onClickDeleteExport(onSuccess: (key: string) => void) {
        try {
            this.startLoading("chat.deleteExportHTML");
            await this._deleteExportHTMLTicketsUseCase.call();
            this.setExportTicketStatus(null);
            onSuccess("chat.success.deleteExportHTML");
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    updateExportationStatus(tickets: TicketEntity[]): void {
        for (const ticket of tickets) {
            if (ticket.chatOperations?.exportStatus && this.user) {
                const exportStatus = ticket.chatOperations?.exportStatus[this.user!.role];
                if (exportStatus) {
                    this.setExportTicketStatus(exportStatus);
                }
            }
        }
    }

    @action
    updateCacheMessagesFromSubs(tickets: TicketEntity[]): void {
        const keys = Object.keys(this.cacheChat);

        const toRemove: string[] = keys.filter(key =>
            !tickets.some(ticket => ticket.id === key)
        );

        for (const key of toRemove) {
            if (this.activeRoute === ActiveRouteEnum.chat) {
                delete this.cacheChat[key];
            }
        }

        for (const ticket of tickets) {
            if (ticket.lastMessage) {
                this.setCacheChat(ticket, [ticket.lastMessage!], 0);
            }
        }
    }

    @action
    removeUnEligibleTable(tickets: TicketEntity[], table: ListPaginationEntity<TicketEntity>): void {
        const tableData = table.data;
        const toRemove: string[] = tableData.filter(item =>
            !tickets.some(ticket => ticket.id === item.id)
        ).map(item => item.id);

        for (let i = tableData.length - 1; i >= 0; i--) {
            const item = tableData[i];
            const index = toRemove.findIndex((entry) => entry == item.id);
            if (index > -1) {
                if (this.currentChat?.id === item.id) {
                    this.eraseChat();
                }
                tableData.splice(i, 1);
            }
        }

    }

    @action
    private setMessagesToActiveChat(data: TicketEntity[]): void {
        const target: TicketEntity | null = this.getTargetTicket(data);
        if (target) {
            if (target.lastMessage) {
                this.updateMessage(target, [target.lastMessage], true, true);
            }
            if (target.messages.length) {
                this.updateMessage(target, target.messages, true, true);
            }
        }
    }

    @action
    private setChatToActiveChatFromSubs(data: TicketEntity[]): void {
        const target: TicketEntity | null = this.getTargetTicket(data);
        if (target) {
            this.setChat(target, false);
        }
    }

    @action
    observerChats = (id?: string): void => {
        this.subscriptionChats?.unsubscribe();
        this.subscriptionChats = this._subscribeChatsUseCase.call(id, this.currentChat).subscribe({
            next: (tickets) => {
                this.preventUnauthorizedAccessChat(tickets);
                this.updateCacheMessagesFromSubs(tickets);
                this.setChatToActiveChatFromSubs(tickets);
                this.updateExportationStatus(tickets);
                this.setMessagesToActiveChat(tickets);
                if (this.activeRoute === ActiveRouteEnum.chat) {
                    this.handleChangesByChats(tickets);
                }
                this.informReadMessagesIfNeeded(this.getTargetTicket(tickets)?.messages).then();
            },
            error: (err: any) => {
                toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
                this.stopLoading();
            }
        });
    }

    @action
    disposeSubscriptionDependant(ticket: TicketEntity | undefined) {
        if (ticket && !ticket.hasActiveStatus) {
            this.leaveTicket();
        }
    }

    @action
    handleChangesByChats(tickets: TicketEntity[]) {
        this.handleChangeListWaiting(tickets);
        this.handleChangeListAttending(tickets);
        this.initChatDependant(tickets);
        this.notificationAudioNewMessage(tickets);
    }

    @action
    initialize = async (
        userLogged: UserEntity,
        activeRoute: ActiveRouteEnum,
        id?: string,
        navigate?: NavigateFunction,
        cancelTokenSource?: CancelTokenSource
    ): Promise<void> => {
        try {
            localStorage.removeItem(ConstantsKeys.draftMessageCache);
            this.setLoadingGetCurrentStateExport(true);
            this.user = userLogged;
            this.navigate = navigate;
            this.setActiveRoute(activeRoute);
            if (this.activeRoute === ActiveRouteEnum.chat) {
                initializeConfigurationLocalStorage(RoutesEnum.chatdefault);
                this.notificationWaiting = new Howl({
                    src: [soundNotificationWaitingList],
                    loop: true,
                    volume: 1,
                });

                this.notificationNewMessage = new Howl({
                    src: [soundNotificationMessage],
                    volume: 0.6,
                });
                await this.listAllChats('', cancelTokenSource);
            }
            this.getCurrentExportState().then();
            if (this.activeRoute === ActiveRouteEnum.ticket && this.currentChat) {
                this.preventUnauthorizedAccessChat([this.currentChat]);
            }

        } catch (error) {
            console.log('error initialize', error);
        }
    }

    @action
    handleClickPushNotification = async (
        ticket: TicketEntity,
    ): Promise<void> => {
        try {
            const ticketAlreadySelected = ticket.id === this.currentChat?.id;
            this.setChat(ticket, !ticketAlreadySelected);
            this.setActiveTabChat(ticket.hasAttendingStatus ? TabsChat.attending : TabsChat.waiting);
        } catch (error) {
            console.error('error initialize', error);
        }
    }

    @action
    setChat(ticket: TicketEntity, eraseMessages = true): void {
        const ticketFromJson = TicketEntity.fromJson(ticket as Record<string, any>);
        const isContactTicket = !!this.contactTicketId && ticketFromJson.id === this.contactTicketId;
        const eligibleSetInRouteChat = (((ticketFromJson.hasActiveStatus || isContactTicket) && this.activeRoute === ActiveRouteEnum.chat) || (this.activeRoute === ActiveRouteEnum.ticket));
        if (eligibleSetInRouteChat) {
            this.currentChat = this.currentChat && this.currentChat.id === ticket.id ? ticketFromJson.preventDeleteRelations(this.currentChat) : ticketFromJson;
        }
        if (eraseMessages) {
            this.eraseMessages();
        }
    }

    @action
    setCurrentChatMessages(value: ListPaginationEntity<TicketMessageEntity>) {
        this.currentChatMessages = value;
    }

    @action
    setActiveRoute(activeRoute: ActiveRouteEnum) {
        this.activeRoute = activeRoute;
    }

    @action
    updateMessage(
        ticket: TicketEntity,
        messages: TicketMessageEntity[],
        unshiftMessage = false,
        sortMessages = false,
        newPaginationMessages: ListPaginationEntity<TicketMessageEntity> | undefined = undefined,
        isActionDelete = false,
        isReplaceMessage = false,
        isNewUpdateAt = false
    ) {
        let updatedMessages: TicketMessageEntity[] | null = null;
        if (isNewUpdateAt) {
            const updatedAt = getFormattedDate();
            updatedMessages = messages.map(message => TicketMessageEntity.fromJson({
                ...message,
                updatedAt
            }));
        }
        const realMessages = updatedMessages ?? messages;
        const page = newPaginationMessages?.page ?? 0;
        this.setCacheChat(ticket, realMessages, page, isActionDelete);
        this.handleSetCurrentChatMessages(realMessages, unshiftMessage, sortMessages, newPaginationMessages, isActionDelete, isReplaceMessage);
    }

    @action
    eraseChat(): void {
        this.currentChat = null;
    }

    @action
    handleRemoveTicketFromListChat(ticket: TicketEntity) {
        this.removeUnEligibleTable(this.tableListWaiting.data.filter((entry) => entry.id !== ticket.id), this.tableListWaiting);
        this.removeUnEligibleTable(this.tableListChats.data.filter((entry) => entry.id !== ticket.id), this.tableListChats);
    }

    @action
    getMessagesCacheFromPage(page: number): TicketMessageEntity[] {
        const cache = this.cacheChat[this.currentChat?.id ?? 'unknown'] ?? {};
        const messages = cache[page] ?? [];
        return messages.map(message => TicketMessageEntity.fromJson(message));
    }

    @action
    eraseMessages(): void {
        if (FeatureToggle.isChatMessagesV2Enabled()) {
            this.eraseMessagesV2();
            return;
        }
        const data = this.getMessagesCacheFromPage(0);
        const initialListPagination = new ListPaginationEntity<TicketMessageEntity>({
            pages: 0,
            page: 0,
            limit: ConstantsKeys.defaultLimit,
            totalRows: 0,
            data,
            hasMore: false,
            hasMoreBottom: false
        });

        if (data && data.length) {
            this.updateMessage(this.currentChat!, data, false, true, initialListPagination, false, true);
        } else {
            this.setCurrentChatMessages(initialListPagination);
        }
        this.handleVirtualizedMessages();
    }

    @action
    handleVirtualizedMessages() {
        const count = Math.floor((window.innerHeight - 302) / 60) + 5;
        if (this.messages.length > count) {
            this.setTargetVirtualizedMessage(this.messages[count]);
        } else {
            this.setTargetVirtualizedMessage(null);
        }
    }

    @action
    setTargetVirtualizedMessage(message: TicketMessageEntity | null) {
        this.targetVirtualizedMessage = message;
    }

    @action
    handleScrollTopVirtualizedMessage() {
        this.setTargetVirtualizedMessage(null);
    }

    @action
    async loadStaticMessages(cancelTokenSource?: CancelTokenSource): Promise<void> {
        if (FeatureToggle.isChatMessagesV2Enabled()) {
            return this.loadStaticMessagesV2(cancelTokenSource);
        }
        try {
            const currentChat = this.currentChat;
            if (currentChat) {
                this.startLoading();
                const response = await this._listChatMessagesUseCase.call(this.currentChat!.id, this.currentChatMessages.page, cancelTokenSource);
                this.updateMessage(currentChat, response.data, true, true, response);
                this.stopLoading();
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    makeStartingAndEndTime(messages: TicketMessageEntity[]): TicketMessageEntity[] {
        const allMessages = [...messages, ...this.reactions];

        for (let i = 0; i < messages.length; i++) {
            const message = messages[i];
            if (
                (message.position && !message.ticket)
                ||
                (message.idTicket === this.currentChat?.id && !this.currentChat?.hasActiveStatus && !message.ticket?.dateClosed)
            ) {
                const newTicket: TicketEntity | undefined = allMessages.find((entry) => (entry.idTicket === message.idTicket && !!entry.ticket))?.ticket as TicketEntity | undefined;
                const ticket = newTicket && newTicket ? TicketEntity.fromJson(newTicket) : undefined;
                message.ticket =
                    (ticket && !!ticket.dateClosed)
                        ?
                        ticket
                        :
                        message.idTicket === this.currentChat?.id
                            ?
                            this.currentChat!
                            :
                            undefined;
            }
        }
        const reactionWithPosition = this.reactions.filter((reactionMessage) => !!reactionMessage.position);
        for (let i = 0; i < reactionWithPosition.length; i++) {
            const reaction = reactionWithPosition[i];
            const messagesById = this.orderMessagesByCreatedAt(messages.filter((message) => message.idTicket === reaction.idTicket));
            if ((reaction.isFirstPosition || reaction.isUniquePosition) && reaction.ticket) {
                const firstMessageById = messagesById[messagesById.length - 1];
                if (firstMessageById) {
                    firstMessageById.position = firstMessageById.position ?? reaction.position;
                    firstMessageById.ticket = reaction.ticket;
                }
            }

            if (reaction.isLastPosition || reaction.isUniquePosition) {
                const lastMessageById = messagesById[0];
                if (lastMessageById) {
                    lastMessageById.position = lastMessageById.position ?? reaction.position;
                    if (!reaction.ticket) {
                        const newTicket: TicketEntity | undefined = allMessages.find((entry) => (entry.idTicket === reaction.idTicket && !!entry.ticket))?.ticket as TicketEntity | undefined;
                        lastMessageById.ticket = newTicket;
                    }
                }
            }
        }
        return messages;
    }

    private getNextPage(scrollPosition: 'top' | 'bottom'): number {
        if (!!this.currentChatMessages.pagesLoad) {
            if (scrollPosition === 'top') {
                return findMaxNumber(this.currentChatMessages.pagesLoad) + 1;
            }
            return (findMinNumber(this.currentChatMessages.pagesLoad) - 1) ?? 0;
        } else {
            if (scrollPosition === 'top') {
                return this.currentChatMessages.page + 1;
            }
            return this.currentChatMessages.page - 1;
        }
    }

    @action
    handleClickOnMessageReply = async (reply: ReplyEntity) => {
        try {
            this.setTargetVirtualizedMessage(null);
            if (FeatureToggle.isChatMessagesV2Enabled()) {
                await this.handleClickOnMessageReplyV2(reply);
                return;
            }
            const page = this.findPageById(reply.messageId);
            const currentChat = this.currentChat!;
            if (page !== null) {
                this.startLoading();
                const cacheMessages = this.getMessagesCacheFromPage(page);
                this.updateMessage(currentChat, cacheMessages, false, true,
                    this.currentChatMessages.copyWith({
                        page: page,
                    }), false, true);
                await sleep(.1);
                this.setIdMessageFocused(reply.messageId);
                this.stopLoading();
            } else {
                this.startLoading();
                this.setForceCleanMessagesCache(true);
                const response = await this._navigateToMessageUseCase.call(reply.messageId, this.currentChat!.number);
                this.updateMessage(currentChat, response.data, false, true, response, false, true);
                this.setForceCleanMessagesCache(false);
                await sleep(.5);
                this.setIdMessageFocused(reply.messageId);
                this.stopLoading();
            }

        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
            this.setForceCleanMessagesCache(false);
        }
    }

    @action
    loadMoreMessages = async (scrollPosition: 'top' | 'bottom'): Promise<boolean> => {
        if (FeatureToggle.isChatMessagesV2Enabled()) {
            return this.loadMoreMessagesV2(scrollPosition);
        }
        try {
            if (!this.loading) {
                this.setScrollPosition(scrollPosition);
                const nextPage = this.getNextPage(scrollPosition);
                const currentChat = this.currentChat!;
                const cacheMessageByNextPage = this.getMessagesCacheFromPage(nextPage);
                this.startLoading();
                if (cacheMessageByNextPage && cacheMessageByNextPage.length > 0) {
                    this.updateMessage(
                        currentChat,
                        cacheMessageByNextPage,
                        false,
                        true,
                        this.currentChatMessages.copyWith({
                            page: nextPage,
                            hasMore: nextPage < (this.currentChatMessages.pages - 1)
                        }),
                    );
                    await sleep(.5);
                    return true;
                } else if (
                    (this.currentChatMessages.hasMore && scrollPosition === 'top') ||
                    (this.currentChatMessages.hasMoreBottom && scrollPosition === 'bottom')
                ) {
                    const response = await this._listChatMessagesUseCase.call(this.currentChat!.id, nextPage);
                    this.updateMessage(currentChat, response.data, false, true, response);
                    await sleep(.5);
                    return true;
                } else {
                    this.stopLoading();
                }
            }
            return false;
        } catch (err: any) {
            this.stopLoading();
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            return false;
        }

    }

    @action
    private resetChatMessagesV2State(): void {
        this.chatMessagesV2 = ChatMessagesV2StateEntity.empty();
    }

    @action
    private setChatMessagesV2(partial: Parameters<ChatMessagesV2StateEntity['copyWith']>[0]): void {
        this.chatMessagesV2 = this.chatMessagesV2.copyWith(partial);
    }

    private getChatMessagesV2Session(openedTicketId: string): ChatMessagesV2SessionEntity {
        const existing = this.chatMessagesV2.sessions[openedTicketId];
        if (existing) {
            return existing;
        }
        const created = ChatMessagesV2SessionEntity.empty();
        this.setChatMessagesV2({
            sessions: {
                ...this.chatMessagesV2.sessions,
                [openedTicketId]: created,
            }
        });
        return created;
    }

    private cloneMessages(messages: TicketMessageEntity[]): TicketMessageEntity[] {
        return messages.map((message) => TicketMessageEntity.fromJson(message));
    }

    private toV2ListPagination(
        response: ListPaginationChatMessagesEntity,
        hasMoreBottom: boolean
    ): ListPaginationEntity<TicketMessageEntity> {
        return new ListPaginationEntity<TicketMessageEntity>({
            data: response.data,
            page: response.page,
            pages: 0,
            totalRows: 0,
            limit: response.limit || ConstantsKeys.chatMessagesV2Limit,
            hasMore: response.hasMore === true,
            hasMoreBottom,
        });
    }

    private getV2CachedPage(openedTicketId: string, ticketId: string, page: number): TicketMessageEntity[] {
        const pages = this.chatMessagesV2.sessions[openedTicketId]?.tickets[ticketId]?.pages ?? {};
        const messages = pages[page] ?? [];
        return this.cloneMessages(messages);
    }

    private findV2LocationByMessageId(messageId: string): { ticketId: string, page: number } | null {
        const openedId = this.currentChat?.id;
        if (!openedId) {
            return null;
        }
        const session = this.chatMessagesV2.sessions[openedId];
        if (!session) {
            return null;
        }
        for (const ticketId of Object.keys(session.tickets)) {
            const pages = session.tickets[ticketId].pages;
            for (const pageKey of Object.keys(pages)) {
                const messages = pages[Number(pageKey)] ?? [];
                if (messages.some((message) => message.id === messageId || message.idExternal === messageId)) {
                    return { ticketId, page: Number(pageKey) };
                }
            }
        }
        return null;
    }

    private isPreviousTicketInSession(openedTicketId: string, previousTicketId: string): boolean {
        const ticket = this.chatMessagesV2.sessions[openedTicketId]?.tickets[previousTicketId];
        if (!ticket) {
            return false;
        }
        return ticket.pages[0] !== undefined;
    }

    private isNextTicketPageInSession(openedTicketId: string, nextTicketId: string, nextTicketPage: number): boolean {
        const ticket = this.chatMessagesV2.sessions[openedTicketId]?.tickets[nextTicketId];
        if (!ticket) {
            return false;
        }
        return ticket.pages[nextTicketPage] !== undefined;
    }

    private listingHasMoreFromSession(openedTicketId: string, ticketId: string, page: number): boolean {
        const cached = this.chatMessagesV2.sessions[openedTicketId]?.tickets[ticketId];
        if (!cached) {
            return false;
        }
        const pageNumbers = Object.keys(cached.pages).map(Number);
        if (pageNumbers.length === 0) {
            return cached.hasMore === true;
        }
        const maxCachedPage = Math.max(...pageNumbers);
        if (page < maxCachedPage) {
            return true;
        }
        return cached.hasMore === true;
    }

    private listingHasNewerFromSession(openedTicketId: string, ticketId: string, page: number): boolean {
        if (page > 0) {
            return true;
        }
        const cached = this.chatMessagesV2.sessions[openedTicketId]?.tickets[ticketId];
        if (cached?.nextTicket != null) {
            return true;
        }
        if (cached?.nextTicketResolved) {
            return false;
        }
        return ticketId !== openedTicketId;
    }

    @action
    private persistV2Page(
        openedTicketId: string,
        response: ListPaginationChatMessagesEntity,
        persistNextTicket = false
    ): void {
        const session = this.getChatMessagesV2Session(openedTicketId);
        const ticketId = response.ticket?.id ?? this.chatMessagesV2.listingTicketId ?? openedTicketId;
        const existing = session.tickets[ticketId];
        const pages = {
            ...(existing?.pages ?? {}),
            [response.page]: this.cloneMessages(response.data),
        };
        const existingPageNumbers = existing ? Object.keys(existing.pages).map(Number) : [];
        const existingMaxPage = existingPageNumbers.length > 0
            ? Math.max(...existingPageNumbers)
            : -1;
        const isLatestCachedPage = response.page >= existingMaxPage;
        const hasMore = isLatestCachedPage || !existing
            ? response.hasMore === true
            : existing.hasMore;
        const previousTicket = isLatestCachedPage || !existing
            ? (response.previousTicket ?? null)
            : existing.previousTicket;
        const previousTicketCached = isLatestCachedPage || !existing
            ? response.previousTicketCached === true
            : existing.previousTicketCached;
        const shouldPersistNext = persistNextTicket && response.page === 0;
        const nextTicket = shouldPersistNext
            ? (response.nextTicket ?? null)
            : (existing?.nextTicket ?? null);
        const nextTicketPage = shouldPersistNext
            ? (response.nextTicketPage ?? 0)
            : (existing?.nextTicketPage ?? 0);
        const nextTicketCached = shouldPersistNext
            ? response.nextTicketCached === true
            : (existing?.nextTicketCached === true);
        const nextTicketResolved = shouldPersistNext
            ? true
            : (existing?.nextTicketResolved === true);
        const nextSession = session.copyWith({
            tickets: {
                ...session.tickets,
                [ticketId]: new ChatMessagesV2TicketCacheEntity({
                    pages,
                    hasMore,
                    ticket: response.ticket,
                    previousTicket,
                    previousTicketCached,
                    nextTicket,
                    nextTicketPage,
                    nextTicketCached,
                    nextTicketResolved,
                }),
            },
            loadedTicketIds: session.loadedTicketIds.includes(ticketId)
                ? session.loadedTicketIds
                : [...session.loadedTicketIds, ticketId],
        });
        this.setChatMessagesV2({
            sessions: {
                ...this.chatMessagesV2.sessions,
                [openedTicketId]: nextSession,
            }
        });
    }

    @action
    private setListingCursorFromResponse(
        response: ListPaginationChatMessagesEntity,
        cursorSide: ChatMessagesV2CursorSide
    ): void {
        const ticketId = response.ticket?.id ?? this.chatMessagesV2.listingTicketId;
        const updateOlder = cursorSide === 'older' || cursorSide === 'both';
        const updateNewer = cursorSide === 'newer' || cursorSide === 'both';
        this.setChatMessagesV2({
            ...(updateOlder ? {
                listingTicketId: ticketId,
                listingPage: response.page,
                listingHasMore: response.hasMore === true,
                previousTicket: response.previousTicket ?? null,
                previousTicketCached: response.previousTicketCached === true,
            } : {}),
            ...(updateNewer ? {
                listingNewerTicketId: ticketId,
                listingNewerPage: response.page,
                listingHasNewer: response.page > 0 || response.nextTicket != null,
                nextTicket: response.page === 0
                    ? (response.nextTicket ?? null)
                    : (cursorSide === 'both' ? null : this.chatMessagesV2.nextTicket),
                nextTicketPage: response.page === 0
                    ? (response.nextTicketPage ?? 0)
                    : (cursorSide === 'both' ? 0 : this.chatMessagesV2.nextTicketPage),
                nextTicketCached: response.page === 0
                    ? response.nextTicketCached === true
                    : (cursorSide === 'both' ? false : this.chatMessagesV2.nextTicketCached),
            } : {}),
        });
        if (updateOlder) {
            this.updateShowLoadPreviousTicketButton();
        }
        if (updateNewer) {
            this.updateShowLoadNextTicketButton();
        }
        this.syncHasMoreBottomFromNewerCursor();
    }

    @action
    private setListingCursorFromSessionTicket(
        openedTicketId: string,
        ticketId: string,
        page: number,
        cursorSide: ChatMessagesV2CursorSide
    ): void {
        const cached = this.chatMessagesV2.sessions[openedTicketId]?.tickets[ticketId];
        const updateOlder = cursorSide === 'older' || cursorSide === 'both';
        const updateNewer = cursorSide === 'newer' || cursorSide === 'both';
        this.setChatMessagesV2({
            ...(updateOlder ? {
                listingTicketId: ticketId,
                listingPage: page,
                listingHasMore: this.listingHasMoreFromSession(openedTicketId, ticketId, page),
                previousTicket: cached?.previousTicket ?? null,
                previousTicketCached: cached?.previousTicketCached === true,
            } : {}),
            ...(updateNewer ? {
                listingNewerTicketId: ticketId,
                listingNewerPage: page,
                listingHasNewer: this.listingHasNewerFromSession(openedTicketId, ticketId, page),
                nextTicket: page === 0
                    ? (cached?.nextTicket ?? null)
                    : (cursorSide === 'both' ? null : this.chatMessagesV2.nextTicket),
                nextTicketPage: page === 0
                    ? (cached?.nextTicketPage ?? 0)
                    : (cursorSide === 'both' ? 0 : this.chatMessagesV2.nextTicketPage),
                nextTicketCached: page === 0
                    ? (cached?.nextTicketCached === true)
                    : (cursorSide === 'both' ? false : this.chatMessagesV2.nextTicketCached),
            } : {}),
        });
        this.currentChatMessages = this.currentChatMessages.copyWith({
            page,
            hasMore: updateOlder ? this.chatMessagesV2.listingHasMore : this.currentChatMessages.hasMore,
            limit: ConstantsKeys.chatMessagesV2Limit,
            pages: 0,
            totalRows: 0,
        });
        if (updateOlder) {
            this.updateShowLoadPreviousTicketButton();
        }
        if (updateNewer) {
            this.updateShowLoadNextTicketButton();
        }
        this.syncHasMoreBottomFromNewerCursor();
    }

    @action
    private syncHasMoreBottomFromNewerCursor(): void {
        this.setCurrentChatMessages(this.currentChatMessages.copyWith({
            hasMoreBottom: this.canAutoScrollNewerV2(),
        }));
        this.updateShowLoadNextTicketButton();
    }

    private canAutoScrollNewerV2(): boolean {
        if (!this.chatMessagesV2.listingHasNewer) {
            return false;
        }
        if (this.chatMessagesV2.listingNewerPage > 0) {
            return true;
        }
        return this.canAutoLoadNextOnce();
    }

    @action
    private resetNewerCursorToLiveEnd(openedId: string | null): void {
        if (openedId) {
            const session = this.getChatMessagesV2Session(openedId);
            this.setChatMessagesV2({
                sessions: {
                    ...this.chatMessagesV2.sessions,
                    [openedId]: session.copyWith({
                        allowAutoLoadNextAfterNavigate: false,
                        didAutoLoadNextAfterNavigate: false,
                    }),
                },
            });
        }
        this.setChatMessagesV2({
            listingNewerTicketId: openedId,
            listingNewerPage: 0,
            listingHasNewer: false,
            nextTicket: null,
            nextTicketPage: 0,
            nextTicketCached: false,
            showLoadNextTicketButton: false,
            loadingNextTicket: false,
        });
        this.syncHasMoreBottomFromNewerCursor();
    }

    private canAutoLoadPreviousOnce(): boolean {
        const openedId = this.currentChat?.id;
        const previousId = this.chatMessagesV2.previousTicket?.id;
        if (!openedId || !previousId || this.chatMessagesV2.listingHasMore) {
            return false;
        }
        if (this.chatMessagesV2.listingTicketId !== openedId) {
            return false;
        }
        const session = this.chatMessagesV2.sessions[openedId];
        if (session?.didAutoLoadPreviousForOpenedTicket) {
            return false;
        }
        if (this.isPreviousTicketInSession(openedId, previousId)) {
            return true;
        }
        if (this.chatMessagesV2.previousTicketCached) {
            return true;
        }
        return this.currentChatMessages.data.length === 0;
    }

    private canAutoLoadNextOnce(): boolean {
        const openedId = this.currentChat?.id;
        if (!openedId) {
            return false;
        }
        const session = this.chatMessagesV2.sessions[openedId];
        if (!session?.allowAutoLoadNextAfterNavigate || session.didAutoLoadNextAfterNavigate) {
            return false;
        }
        if (this.chatMessagesV2.listingNewerPage !== 0) {
            return false;
        }
        const nextId = this.chatMessagesV2.nextTicket?.id;
        if (!nextId) {
            return false;
        }
        if (this.isNextTicketPageInSession(openedId, nextId, this.chatMessagesV2.nextTicketPage)) {
            return true;
        }
        return this.chatMessagesV2.nextTicketCached === true;
    }

    private async maybeAutoLoadNextTicketV2(): Promise<boolean> {
        if (!this.canAutoLoadNextOnce()) {
            this.updateShowLoadNextTicketButton();
            return false;
        }
        return this.loadNextTicketV2(true);
    }

    private async maybeAutoLoadPreviousTicketV2(): Promise<boolean> {
        if (!this.canAutoLoadPreviousOnce()) {
            this.updateShowLoadPreviousTicketButton();
            return false;
        }
        const openedId = this.currentChat?.id;
        const loaded = await this.loadPreviousTicketV2();
        if (openedId) {
            this.markDidAutoLoadPreviousForOpenedTicket(openedId);
        }
        if (loaded) {
            return true;
        }
        this.updateShowLoadPreviousTicketButton();
        return false;
    }

    @action
    private updateShowLoadPreviousTicketButton(): void {
        const openedId = this.currentChat?.id;
        const previousId = this.chatMessagesV2.previousTicket?.id;
        if (!openedId || this.chatMessagesV2.listingHasMore || !previousId) {
            this.setChatMessagesV2({ showLoadPreviousTicketButton: false });
            return;
        }
        if (this.canAutoLoadPreviousOnce()) {
            this.setChatMessagesV2({ showLoadPreviousTicketButton: false });
            return;
        }
        this.setChatMessagesV2({
            showLoadPreviousTicketButton: true,
        });
    }

    @action
    private updateShowLoadNextTicketButton(): void {
        const openedId = this.currentChat?.id;
        const nextId = this.chatMessagesV2.nextTicket?.id;
        const session = openedId ? this.chatMessagesV2.sessions[openedId] : null;
        if (
            !openedId
            || !session?.allowAutoLoadNextAfterNavigate
            || this.chatMessagesV2.listingNewerPage > 0
            || !nextId
        ) {
            this.setChatMessagesV2({ showLoadNextTicketButton: false });
            return;
        }
        if (this.canAutoLoadNextOnce()) {
            this.setChatMessagesV2({ showLoadNextTicketButton: false });
            return;
        }
        this.setChatMessagesV2({
            showLoadNextTicketButton: true,
        });
    }

    @action
    private markDidAutoLoadPreviousForOpenedTicket(openedId: string): void {
        const session = this.getChatMessagesV2Session(openedId);
        this.setChatMessagesV2({
            sessions: {
                ...this.chatMessagesV2.sessions,
                [openedId]: session.copyWith({
                    didAutoLoadPreviousForOpenedTicket: true,
                }),
            }
        });
    }

    @action
    private markDidAutoLoadNextAfterNavigate(openedId: string): void {
        const session = this.getChatMessagesV2Session(openedId);
        this.setChatMessagesV2({
            sessions: {
                ...this.chatMessagesV2.sessions,
                [openedId]: session.copyWith({
                    didAutoLoadNextAfterNavigate: true,
                }),
            }
        });
        this.updateShowLoadNextTicketButton();
        this.syncHasMoreBottomFromNewerCursor();
    }

    @action
    private setNavigateAwayFlagsAfterReply(openedId: string, ticketId: string, page: number): void {
        const session = this.getChatMessagesV2Session(openedId);
        const isLiveEndOfOpenedTicket = ticketId === openedId && page === 0;
        this.setChatMessagesV2({
            sessions: {
                ...this.chatMessagesV2.sessions,
                [openedId]: session.copyWith({
                    allowAutoLoadNextAfterNavigate: !isLiveEndOfOpenedTicket,
                    didAutoLoadNextAfterNavigate: false,
                }),
            },
        });
        this.updateShowLoadNextTicketButton();
        this.syncHasMoreBottomFromNewerCursor();
    }

    @action
    private resetNavigateFlagsForOpenedTicket(openedId: string): void {
        const session = this.getChatMessagesV2Session(openedId);
        this.setChatMessagesV2({
            sessions: {
                ...this.chatMessagesV2.sessions,
                [openedId]: session.copyWith({
                    didAutoLoadPreviousForOpenedTicket: false,
                    allowAutoLoadNextAfterNavigate: false,
                    didAutoLoadNextAfterNavigate: false,
                }),
            },
            showLoadNextTicketButton: false,
            loadingNextTicket: false,
        });
    }

    @action
    private applyV2PageToList(
        response: ListPaginationChatMessagesEntity,
        openedTicketId: string,
        isReplaceMessage: boolean,
        unshiftMessage: boolean,
        cursorSide: ChatMessagesV2CursorSide
    ): void {
        const pagination = this.toV2ListPagination(
            response,
            cursorSide === 'older'
                ? this.chatMessagesV2.listingHasNewer === true
                : (response.page > 0 || response.nextTicket != null)
        );
        const isOpenedTicket = (response.ticket?.id ?? openedTicketId) === openedTicketId;
        if (isOpenedTicket && this.currentChat) {
            this.updateMessage(
                this.currentChat,
                response.data,
                unshiftMessage,
                true,
                pagination,
                false,
                isReplaceMessage
            );
        } else {
            this.handleSetCurrentChatMessages(
                response.data,
                unshiftMessage,
                true,
                pagination,
                false,
                isReplaceMessage
            );
        }
        this.setListingCursorFromResponse(response, cursorSide);
    }

    @action
    private applyCachedV2Page(
        openedTicketId: string,
        ticketId: string,
        page: number,
        isReplaceMessage: boolean,
        cursorSide: ChatMessagesV2CursorSide
    ): boolean {
        const cachedPage = this.getV2CachedPage(openedTicketId, ticketId, page);
        if (cachedPage.length === 0) {
            return false;
        }
        const pagination = new ListPaginationEntity<TicketMessageEntity>({
            data: cachedPage,
            page,
            pages: 0,
            totalRows: 0,
            limit: ConstantsKeys.chatMessagesV2Limit,
            hasMore: this.listingHasMoreFromSession(openedTicketId, ticketId, page),
            hasMoreBottom: this.listingHasNewerFromSession(openedTicketId, ticketId, page),
        });
        if (ticketId === openedTicketId && this.currentChat) {
            this.updateMessage(this.currentChat, cachedPage, false, true, pagination, false, isReplaceMessage);
        } else {
            this.handleSetCurrentChatMessages(cachedPage, false, true, pagination, false, isReplaceMessage);
        }
        this.setListingCursorFromSessionTicket(openedTicketId, ticketId, page, cursorSide);
        return true;
    }

    @action
    private applyPreviousTicketFromSession(openedTicketId: string, previousTicketId: string): boolean {
        if (!this.isPreviousTicketInSession(openedTicketId, previousTicketId)) {
            return false;
        }
        return this.applyCachedV2Page(openedTicketId, previousTicketId, 0, false, 'older');
    }

    @action
    private applyNextTicketFromSession(openedTicketId: string, nextTicketId: string, nextTicketPage: number): boolean {
        if (!this.isNextTicketPageInSession(openedTicketId, nextTicketId, nextTicketPage)) {
            return false;
        }
        return this.applyCachedV2Page(openedTicketId, nextTicketId, nextTicketPage, false, 'newer');
    }

    @action
    eraseMessagesV2(): void {
        const data = this.getMessagesCacheFromPage(0);
        const initialListPagination = new ListPaginationEntity<TicketMessageEntity>({
            pages: 0,
            page: 0,
            limit: ConstantsKeys.chatMessagesV2Limit,
            totalRows: 0,
            data,
            hasMore: false,
            hasMoreBottom: false
        });

        if (data && data.length) {
            this.updateMessage(this.currentChat!, data, false, true, initialListPagination, false, true);
        } else {
            this.setCurrentChatMessages(initialListPagination);
        }

        const openedId = this.currentChat?.id;
        if (openedId) {
            const cached = this.chatMessagesV2.sessions[openedId]?.tickets[openedId];
            this.setChatMessagesV2({
                listingTicketId: openedId,
                listingPage: 0,
                listingHasMore: this.listingHasMoreFromSession(openedId, openedId, 0),
                previousTicket: cached?.previousTicket ?? null,
                previousTicketCached: cached?.previousTicketCached ?? false,
                showLoadPreviousTicketButton: false,
                listingInFlightKey: null,
                loadingPreviousTicket: false,
            });
            this.resetNewerCursorToLiveEnd(openedId);
            this.updateShowLoadPreviousTicketButton();
        } else {
            this.setChatMessagesV2({
                listingTicketId: null,
                listingPage: 0,
                listingHasMore: false,
                previousTicket: null,
                previousTicketCached: false,
                showLoadPreviousTicketButton: false,
            });
            this.resetNewerCursorToLiveEnd(null);
        }
        this.handleVirtualizedMessages();
    }

    @action
    handleMoveCursorToFinalV2 = (): void => {
        const openedId = this.currentChat?.id;
        if (!openedId || !this.currentChat) {
            return;
        }
        const messages = this.getMessagesCacheFromPage(0);
        this.updateMessage(
            this.currentChat,
            messages,
            false,
            true,
            this.currentChatMessages.copyWith({
                page: 0,
                hasMore: this.listingHasMoreFromSession(openedId, openedId, 0),
                hasMoreBottom: false,
                limit: ConstantsKeys.chatMessagesV2Limit,
                pages: 0,
                totalRows: 0,
            }),
            false,
            true,
        );
        this.setListingCursorFromSessionTicket(openedId, openedId, 0, 'older');
        this.resetNewerCursorToLiveEnd(openedId);
        this.handleVirtualizedMessages();
    }

    @action
    async loadStaticMessagesV2(cancelTokenSource?: CancelTokenSource): Promise<void> {
        try {
            const currentChat = this.currentChat;
            if (!currentChat) {
                return;
            }
            const openedId = currentChat.id;
            this.resetNavigateFlagsForOpenedTicket(openedId);
            const inFlightKey = `${openedId}:0`;
            this.setChatMessagesV2({ listingInFlightKey: inFlightKey });
            this.startLoading();
            const response = await this._listChatMessagesV2UseCase.call(
                openedId,
                0,
                ConstantsKeys.chatMessagesV2Limit,
                cancelTokenSource
            );
            if (!response || this.currentChat?.id !== openedId || this.chatMessagesV2.listingInFlightKey !== inFlightKey) {
                return;
            }
            this.persistV2Page(openedId, response);
            this.applyV2PageToList(response, openedId, false, true, 'older');
            this.resetNewerCursorToLiveEnd(openedId);
            this.setChatMessagesV2({ listingInFlightKey: null });
            if (!(await this.maybeAutoLoadPreviousTicketV2())) {
                this.stopLoading();
            }
        } catch (err: any) {
            this.setChatMessagesV2({ listingInFlightKey: null });
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    loadMoreMessagesV2 = async (scrollPosition: 'top' | 'bottom'): Promise<boolean> => {
        try {
            if (this.loading || this.chatMessagesV2.listingInFlightKey || !this.currentChat) {
                return false;
            }
            if (scrollPosition === 'top') {
                return this.loadOlderMessagesV2();
            }
            if (scrollPosition === 'bottom') {
                return this.loadNewerMessagesV2();
            }
            return false;
        } catch (err: any) {
            this.setChatMessagesV2({ listingInFlightKey: null });
            this.stopLoading();
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            return false;
        }
    }

    @action
    private loadOlderMessagesV2 = async (): Promise<boolean> => {
        if (!this.chatMessagesV2.listingHasMore) {
            return this.maybeAutoLoadPreviousTicketV2();
        }
        if (!this.chatMessagesV2.listingTicketId || !this.currentChat) {
            return false;
        }
        const openedId = this.currentChat.id;
        const listingTicketId = this.chatMessagesV2.listingTicketId;
        const nextPage = this.chatMessagesV2.listingPage + 1;
        const inFlightKey = `${listingTicketId}:${nextPage}:up`;
        this.setScrollPosition('top');
        this.setChatMessagesV2({ listingInFlightKey: inFlightKey });

        const canUseCache = this.getV2CachedPage(openedId, listingTicketId, nextPage).length > 0;
        if (canUseCache) {
            this.applyCachedV2Page(openedId, listingTicketId, nextPage, false, 'older');
            if (!this.chatMessagesV2.listingHasMore) {
                this.setChatMessagesV2({ listingInFlightKey: null });
            }
            if (!(await this.maybeAutoLoadPreviousTicketV2())) {
                await sleep(.5);
            }
            if (this.chatMessagesV2.listingInFlightKey) {
                this.setChatMessagesV2({ listingInFlightKey: null });
            }
            if (!this.chatMessagesV2.listingHasMore) {
                this.updateShowLoadPreviousTicketButton();
            }
            return true;
        }

        this.startLoading();

        const response = await this._listChatMessagesV2UseCase.call(
            listingTicketId,
            nextPage,
            ConstantsKeys.chatMessagesV2Limit
        );
        if (!response || this.currentChat?.id !== openedId || this.chatMessagesV2.listingInFlightKey !== inFlightKey) {
            this.setChatMessagesV2({ listingInFlightKey: null });
            this.stopLoading();
            return false;
        }
        this.persistV2Page(openedId, response);
        this.applyV2PageToList(response, openedId, false, false, 'older');
        if (!this.chatMessagesV2.listingHasMore) {
            this.setChatMessagesV2({ listingInFlightKey: null });
        }
        if (!(await this.maybeAutoLoadPreviousTicketV2())) {
            await sleep(.5);
            this.stopLoading();
        }
        if (this.chatMessagesV2.listingInFlightKey) {
            this.setChatMessagesV2({ listingInFlightKey: null });
        }
        if (!this.chatMessagesV2.listingHasMore) {
            this.updateShowLoadPreviousTicketButton();
        }
        return true;
    }

    @action
    private loadNewerMessagesV2 = async (): Promise<boolean> => {
        if (!this.chatMessagesV2.listingHasNewer || !this.currentChat) {
            return false;
        }
        const listingTicketId = this.chatMessagesV2.listingNewerTicketId ?? this.chatMessagesV2.listingTicketId;
        if (!listingTicketId) {
            return false;
        }
        if (this.chatMessagesV2.listingNewerPage > 0) {
            return this.loadNewerPageOfCurrentTicketV2(listingTicketId, this.chatMessagesV2.listingNewerPage - 1);
        }
        if (this.chatMessagesV2.nextTicket?.id) {
            if (this.canAutoLoadNextOnce()) {
                return this.maybeAutoLoadNextTicketV2();
            }
            this.updateShowLoadNextTicketButton();
            return false;
        }
        return this.resolveNextTicketAndLoadV2(listingTicketId);
    }

    @action
    private loadNewerPageOfCurrentTicketV2 = async (listingTicketId: string, page: number): Promise<boolean> => {
        const openedId = this.currentChat?.id;
        if (!openedId || !this.currentChat) {
            return false;
        }
        const direction: 'up' | 'down' | undefined = page === 0 ? 'down' : undefined;
        const cached = this.chatMessagesV2.sessions[openedId]?.tickets[listingTicketId];
        const cachedPage = this.getV2CachedPage(openedId, listingTicketId, page);
        const canUseCache = cachedPage.length > 0 && (page > 0 || cached?.nextTicketResolved === true);
        const inFlightKey = `${listingTicketId}:${page}:down`;
        this.setScrollPosition('bottom');
        this.setChatMessagesV2({ listingInFlightKey: inFlightKey });
        this.startLoading();

        if (canUseCache) {
            this.applyCachedV2Page(openedId, listingTicketId, page, false, 'newer');
            this.setChatMessagesV2({ listingInFlightKey: null });
            if (page === 0) {
                if (await this.maybeAutoLoadNextTicketV2()) {
                    return true;
                }
            }
            await sleep(.5);
            this.stopLoading();
            return true;
        }

        const response = await this._listChatMessagesV2UseCase.call(
            listingTicketId,
            page,
            ConstantsKeys.chatMessagesV2Limit,
            undefined,
            direction
        );
        if (!response || this.currentChat?.id !== openedId || this.chatMessagesV2.listingInFlightKey !== inFlightKey) {
            this.setChatMessagesV2({ listingInFlightKey: null });
            this.stopLoading();
            return false;
        }
        this.persistV2Page(openedId, response, page === 0);
        this.applyV2PageToList(response, openedId, false, false, 'newer');
        this.setChatMessagesV2({ listingInFlightKey: null });
        if (page === 0) {
            if (await this.maybeAutoLoadNextTicketV2()) {
                return true;
            }
        }
        await sleep(.5);
        this.stopLoading();
        return true;
    }

    @action
    private resolveNextTicketAndLoadV2 = async (listingTicketId: string): Promise<boolean> => {
        const openedId = this.currentChat?.id;
        if (!openedId || !this.currentChat) {
            return false;
        }
        const inFlightKey = `${listingTicketId}:0:down`;
        this.setScrollPosition('bottom');
        this.setChatMessagesV2({ listingInFlightKey: inFlightKey });
        this.startLoading();
        const response = await this._listChatMessagesV2UseCase.call(
            listingTicketId,
            0,
            ConstantsKeys.chatMessagesV2Limit,
            undefined,
            'down'
        );
        if (!response || this.currentChat?.id !== openedId || this.chatMessagesV2.listingInFlightKey !== inFlightKey) {
            this.setChatMessagesV2({ listingInFlightKey: null });
            this.stopLoading();
            return false;
        }
        this.persistV2Page(openedId, response, true);
        this.setListingCursorFromResponse(response, 'newer');
        this.setChatMessagesV2({ listingInFlightKey: null });
        if (this.chatMessagesV2.nextTicket?.id) {
            if (await this.maybeAutoLoadNextTicketV2()) {
                return true;
            }
        }
        await sleep(.5);
        this.stopLoading();
        return true;
    }

    @action
    loadNextTicketV2 = async (fromAutoLoad = false): Promise<boolean> => {
        const openedId = this.currentChat?.id;
        const nextId = this.chatMessagesV2.nextTicket?.id;
        const nextPage = this.chatMessagesV2.nextTicketPage;
        if (!openedId || !nextId || this.chatMessagesV2.listingInFlightKey) {
            return false;
        }
        if (nextId === this.chatMessagesV2.listingNewerTicketId && nextPage === this.chatMessagesV2.listingNewerPage) {
            return false;
        }
        const inFlightKey = `${nextId}:${nextPage}:down`;
        this.setChatMessagesV2({
            listingInFlightKey: inFlightKey,
            loadingNextTicket: true,
        });
        if (fromAutoLoad) {
            this.setScrollPosition('bottom');
            this.startLoading();
        }
        try {
            if (this.isNextTicketPageInSession(openedId, nextId, nextPage)) {
                this.applyNextTicketFromSession(openedId, nextId, nextPage);
                this.setChatMessagesV2({
                    listingInFlightKey: null,
                    loadingNextTicket: false,
                });
                if (fromAutoLoad) {
                    this.markDidAutoLoadNextAfterNavigate(openedId);
                } else {
                    this.updateShowLoadNextTicketButton();
                }
                if (fromAutoLoad) {
                    await sleep(.5);
                    this.stopLoading();
                }
                return true;
            }
            const direction: 'up' | 'down' | undefined = nextPage === 0 ? 'down' : undefined;
            const response = await this._listChatMessagesV2UseCase.call(
                nextId,
                nextPage,
                ConstantsKeys.chatMessagesV2Limit,
                undefined,
                direction
            );
            if (!response || this.currentChat?.id !== openedId || this.chatMessagesV2.listingInFlightKey !== inFlightKey) {
                this.setChatMessagesV2({ listingInFlightKey: null, loadingNextTicket: false });
                if (fromAutoLoad) {
                    this.stopLoading();
                }
                return false;
            }
            this.persistV2Page(openedId, response, nextPage === 0);
            this.applyV2PageToList(response, openedId, false, false, 'newer');
            this.setChatMessagesV2({
                listingInFlightKey: null,
                loadingNextTicket: false,
            });
            if (fromAutoLoad) {
                this.markDidAutoLoadNextAfterNavigate(openedId);
            } else {
                this.updateShowLoadNextTicketButton();
            }
            if (fromAutoLoad) {
                await sleep(.5);
                this.stopLoading();
            }
            return true;
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            if (fromAutoLoad) {
                this.stopLoading();
            }
            return false;
        } finally {
            this.setChatMessagesV2({
                listingInFlightKey: null,
                loadingNextTicket: false,
            });
        }
    }

    @action
    private async handleClickOnMessageReplyV2(reply: ReplyEntity): Promise<void> {
        const currentChat = this.currentChat;
        if (!currentChat) {
            return;
        }
        const openedId = currentChat.id;
        const location = this.findV2LocationByMessageId(reply.messageId);
        if (location) {
            this.startLoading();
            this.applyCachedV2Page(openedId, location.ticketId, location.page, true, 'both');
            this.setNavigateAwayFlagsAfterReply(openedId, location.ticketId, location.page);
            await sleep(.5);
            this.setIdMessageFocused(reply.messageId);
            this.stopLoading();
            return;
        }
        this.startLoading();
        this.setForceCleanMessagesCache(true);
        const response = await this._navigateToMessageV2UseCase.call(
            reply.messageId,
            ConstantsKeys.chatMessagesV2Limit
        );
        if (!response || this.currentChat?.id !== openedId) {
            this.setForceCleanMessagesCache(false);
            this.stopLoading();
            return;
        }
        this.persistV2Page(openedId, response, response.page === 0);
        this.applyV2PageToList(response, openedId, true, false, 'both');
        this.setNavigateAwayFlagsAfterReply(openedId, response.ticket?.id ?? openedId, response.page);
        this.setForceCleanMessagesCache(false);
        await sleep(.5);
        this.setIdMessageFocused(reply.messageId);
        this.stopLoading();
    }

    @action
    loadPreviousTicketV2 = async (): Promise<boolean> => {
        const openedId = this.currentChat?.id;
        const previousId = this.chatMessagesV2.previousTicket?.id;
        if (!openedId || !previousId || this.chatMessagesV2.listingInFlightKey) {
            return false;
        }
        const inFlightKey = `${previousId}:0:up`;
        this.setChatMessagesV2({
            listingInFlightKey: inFlightKey,
            loadingPreviousTicket: true,
        });
        this.setScrollPosition('top');
        this.startLoading();
        try {
            if (this.isPreviousTicketInSession(openedId, previousId)) {
                this.applyPreviousTicketFromSession(openedId, previousId);
                this.setChatMessagesV2({
                    listingInFlightKey: null,
                    loadingPreviousTicket: false,
                });
                await sleep(.5);
                this.stopLoading();
                return true;
            }
            const response = await this._listChatMessagesV2UseCase.call(
                previousId,
                0,
                ConstantsKeys.chatMessagesV2Limit
            );
            if (!response || this.currentChat?.id !== openedId || this.chatMessagesV2.listingInFlightKey !== inFlightKey) {
                return false;
            }
            this.persistV2Page(openedId, response);
            this.applyV2PageToList(response, openedId, false, false, 'older');
            this.updateShowLoadPreviousTicketButton();
            this.setChatMessagesV2({
                listingInFlightKey: null,
                loadingPreviousTicket: false,
            });
            await sleep(.5);
            this.stopLoading();
            return true;
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
            return false;
        } finally {
            this.setChatMessagesV2({
                listingInFlightKey: null,
                loadingPreviousTicket: false,
            });
        }
    }

    @action
    private handleSetCurrentChatMessages(
        newMessages: TicketMessageEntity[],
        unshiftMessage: boolean = false,
        sortMessages: boolean = true,
        newPaginationMessages?: ListPaginationEntity<TicketMessageEntity>,
        isActionDelete = false,
        isReplaceMessage = false
    ): void {
        const canUpdateMessage: boolean = !!newMessages.find((message: TicketMessageEntity) => message.users![this.currentChat?.userClient?.id ?? 'noUser']) ?? false;
        if (canUpdateMessage) {
            const messages: TicketMessageEntity[] = this.currentChatMessages.data;

            for (const message of newMessages) {
                const index = messages.findIndex((element) => element.id === message.id);
                if (index > -1) {
                    if (isActionDelete) {
                        messages.splice(index, 1);
                    } else {
                        messages[index] = messages[index].getMessagePreventDefaultCache(message);
                    }
                } else {
                    if (unshiftMessage) {
                        messages.unshift(message);
                    } else {
                        messages.push(message);
                    }
                }
            }

            if (sortMessages) {
                messages.sort((a, b) => (new Date(b.realCreatedAt).getTime() - (new Date(a.realCreatedAt).getTime())));
            }

            const currentPage = this.currentChatMessages.page;
            const newPage = newPaginationMessages?.page;
            const page = newPage ?? currentPage;
            const hasMoreBottom = !!(isReplaceMessage && (newPage && newPage > 0));
            this.setCurrentChatMessages(this.currentChatMessages.copyWith({
                data: isReplaceMessage ? newMessages : messages,
                page: newPaginationMessages?.page ?? this.currentChatMessages.page,
                pages: newPaginationMessages?.pages ?? this.currentChatMessages.pages,
                totalRows: newPaginationMessages?.totalRows ?? this.currentChatMessages.totalRows,
                limit: newPaginationMessages?.limit ?? this.currentChatMessages.limit,
                hasMore: newPaginationMessages?.hasMore ?? this.currentChatMessages.hasMore,
                hasMoreBottom: newPaginationMessages?.hasMoreBottom !== undefined
                    ? newPaginationMessages.hasMoreBottom
                    : (hasMoreBottom ? true : newPage === 0 ? false : this.currentChatMessages.hasMoreBottom),
                pagesLoad: isReplaceMessage ? [page] : this.addPagesLoad(page)
            }));
        }
    }

    @action
    async sendMessage(
        body: BodyMessage,
    ): Promise<void> {
        return new Promise<void>(async (resolve, _) => {
            try {
                this.updateMessage(body.ticket, [body.message], true, false);
                if (body.message.isFileMessage && body.message.isUnsaved()) {
                    body.message = await this.uploadFile(body);
                    const messages = this.cacheChat[body.ticket.id][0];
                    await when(
                        () => {
                            const index = messages.findIndex(msg => msg.id === body.message.id);
                            return messages.slice(0, index).every(previousMessage =>
                                previousMessage.createdAt || previousMessage.hasSendError
                            );
                        },
                        { timeout: 60000 * 3 }
                    ).catch((error) => console.error(`Mensagem não enviada no tempo limite ${error}`));
                }
                await this.writeMessage(body);
                return resolve();
            } catch (error: any) {
                if (error.message === 'ticket_serving') {
                    if (this.activeRoute === ActiveRouteEnum.chat) {
                        this.handleRemoveTicketFromListChat(body.ticket);
                    }
                }
                this.handleWriteMessageError(body.ticket, body.message, error);
                const statusCode = error instanceof ServerError ? error.statusCode : undefined;
                if (statusCode && statusCode !== 503) {
                    await this.listChatMessagesAfterLastSubscription([body.ticket]);
                }
                toastMessage.error(translate(`server_messages.${error?.code ?? error?.message ?? 'unknown'}`));
            }
        });
    }

    handleWriteMessageError(ticket: TicketEntity, message: TicketMessageEntity, error?: DropDeskException): void {
        const statusCode = error instanceof ServerError ? error.statusCode : undefined;
        const shouldWriteMessageOnRetry = !statusCode || statusCode === 503;
        const messageError = message.copyWith({
            status: MessageStatus.ERROR,
            ...(shouldWriteMessageOnRetry ? {} : { statusError: StatusMessageError.sendMessageError }),
        });
        this.updateMessage(ticket, [messageError], undefined, undefined,
            undefined, undefined, undefined, true);
    }


    @action
    writeMessage = async (
        body: BodyMessage,
    ): Promise<TicketMessageEntity> => {
        return new Promise<TicketMessageEntity>(async (resolve, reject) => {
            try {
                let _message = body.message;
                const associateSenderToTicket = !body.ticket.attendant && !_message.isPrivate && body.sender.isAttendant;
                _message = await this._sendMessageUseCase.call(_message, associateSenderToTicket);
                this.updateMessage(body.ticket, [_message], true, false);
                return resolve(_message);
            } catch (error: any) {
                return reject(error);
            }
        });
    }

    @action
    removeReaction = async (
        messageReacted: TicketMessageEntity,
        reaction: TicketMessageEntity,
        currentTicket: TicketEntity,
    ): Promise<void> => {
        try {
            const reactionToRemove = reaction.copyWith({
                deleted: true,
                reactionMessage: new ReactionMessageEntity({
                    text: '',
                    messageReactedId: reaction.reactionMessage!.messageReactedId,
                    fromMe: reaction.reactionMessage!.fromMe,
                    participantId: reaction.reactionMessage?.participantId ?? '',
                })
            });
            this.updateMessage(currentTicket, [reactionToRemove], undefined,
                undefined, undefined, undefined, undefined, true);
            this.handleUpdateTicket(currentTicket.copyWith({
                lastMessage: this.currentChatMessages.data[0],
            }));
            await this._reactionMessageUseCase.call(messageReacted.id, reactionToRemove.id, '');

        } catch (err: any) {
            this.updateMessage(currentTicket, [reaction], undefined, undefined,
                undefined, false, undefined, true);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    @action
    async handleAddReaction(
        messageToReacted: TicketMessageEntity,
        reactionText: string,
        userLogged: UserEntity,
        currentTicket: TicketEntity
    ): Promise<void> {
        this.setLastUsedReactions(reactionText);
        const reactionMessage: TicketMessageEntity | undefined = this.getReactionByUser(userLogged, messageToReacted);
        if (reactionMessage && reactionText === reactionMessage?.reactionMessage?.text) {
            await this.removeReaction(messageToReacted, reactionMessage, currentTicket);
        } else {
            await this.insertReaction(messageToReacted, reactionText, userLogged, currentTicket);
        }
    }


    private setLastUsedReactions = (reaction: string) => {
        const hasReaction: boolean = this.lastUsedReactions.filter((entry) => entry === reaction).length > 0;
        if (!hasReaction) {
            this.lastUsedReactions.pop();
            const lastUsedReactions = [reaction, ...this.lastUsedReactions];
            this.lastUsedReactions = lastUsedReactions;
            localStorage.setItem(ConstantsKeys.lastUsedReactions, JSON.stringify(lastUsedReactions));
        }
    }

    private insertReaction = async (
        messageReacted: TicketMessageEntity,
        reaction: string,
        userLogged: UserEntity,
        currentTicket: TicketEntity
    ): Promise<void> => {
        const body = new BodyMessage({
            message: newMessage(currentTicket.id),
            ticket: this.currentChat!,
            sender: userLogged
        });
        const hasReaction = this.getReactionByUser(userLogged, messageReacted);
        body.message = body.message.copyWith({
            ...this.prepareToSend(body),
            id: hasReaction ? hasReaction.id : body.message.id,
            type: MessageChatType.reactionMessage,
            reactionMessage: new ReactionMessageEntity({
                text: reaction,
                messageReactedId: messageReacted.idExternal ?? messageReacted.id,
                fromMe: true,
                participantId: hasReaction && hasReaction.reactionMessage?.participantId ? hasReaction.reactionMessage?.participantId : ''
            })
        });
        try {
            this.updateMessage(body.ticket, [body.message], undefined, undefined, undefined, undefined, undefined, true);
            this.handleUpdateTicket(currentTicket.copyWith({
                lastMessage: body.message,
            }));
            await this._reactionMessageUseCase.call(messageReacted.id, body.message.id, reaction);
        } catch (err: any) {
            this.updateMessage(body.ticket, [body.message], undefined, undefined, undefined, true, undefined, true);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    private getReactionByUser = (userLogged: UserEntity, messageToReacted: TicketMessageEntity): TicketMessageEntity | undefined => {
        return this.reactions.find((entry) => entry.senderId === userLogged.id && (messageToReacted.idExternal === entry.reactionMessage?.messageReactedId || messageToReacted.id === entry.reactionMessage?.messageReactedId));
    }

    @action
    editMessage = async (
        editText: string,
        currentMessage: TicketMessageEntity,
        currentTicket: TicketEntity
    ): Promise<void> => {
        try {
            if (!!editText && currentMessage.getTextEditable !== editText) {
                const editMessage = currentMessage.setEditableText(editText);
                this.updateMessage(
                    currentTicket, [editMessage], undefined, undefined,
                    undefined, undefined, undefined, true
                );
                await this._editMessageUseCase.call(currentMessage.id, editText);
            }
        } catch (err: any) {
            this.updateMessage(currentTicket, [currentMessage], undefined, undefined,
                undefined, undefined, undefined, true);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    @action
    uploadFile = (
        body: BodyMessage,
    ): Promise<TicketMessageEntity> => {
        return new Promise<TicketMessageEntity>(async (resolve, reject) => {
            let _message = body.message;
            this.subscriptionUploadFile = this._subscriptionUploadFileUseCase.call(_message).subscribe({
                next: (response) => {
                    _message = response;
                    this.updateMessage(body.ticket, [response], undefined, undefined,
                        undefined, undefined, undefined, true);
                },
                error: (error: FirebaseStorageError) => {
                    return reject(error);
                },
                complete: () => {
                    this.updateMessage(body.ticket, [_message.removeUploadOptions().getMessagePreventDefaultCache(_message)],
                        undefined, undefined, undefined, undefined, undefined, true);
                    const newBody = new BodyMessage({
                        ticket: body.ticket,
                        sender: body.sender,
                        message: _message
                    });
                    return resolve(newBody.message);
                }
            })
        });
    }

    @action
    delete = async (currentTicket: TicketEntity, message: TicketMessageEntity): Promise<void> => {
        try {
            message.deleted = true;
            this.updateMessage(currentTicket, [message], undefined, undefined,
                undefined, undefined, undefined, true);
            await this._deleteMessageUseCase.call(message.id);
        } catch (err: any) {
            message.deleted = false;
            this.updateMessage(currentTicket, [message]);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    @action
    parseFileToMessage = (ticket: TicketEntity, fileMessages: FileMessageEntity[], sender: UserEntity): TicketMessageEntity[] => {
        return fileMessages.map((fileMessage) => {
            const type: MessageChatType = getMessageTypeFromFile(fileMessage);
            const {
                imageMessage,
                videoMessage,
                documentMessage
            } = convertFileToMessageEntity(type, fileMessage);


            return new TicketMessageEntity({
                deleted: false,
                idCompany: getIdCompanyByLocalStorage(),
                idTicket: ticket.id,
                id: generateUUIDV4(),
                sender,
                senderId: sender.id,
                audioMessage: null,
                videoMessage,
                imageMessage,
                documentMessage,
                blobURL: fileMessage.blobURL,
                textMessage: null,
                uploadOptions: new UploadOptionsEntity({
                    file: fileMessage,
                    progress: 0,
                }),
                type,
                isPrivate: fileMessage.isPrivate,
                fromMe: true,
                status: MessageStatus.PENDING,
                createdAtLocal: new Date().toISOString(),
                showOriginal: true,
            })
        }
        );
    }

    @action
    parseAudioToMessage = async (
        ticket: TicketEntity,
        audioRecorder: AudioRecordEntity,
        sender: UserEntity,
        message: TicketMessageEntity
    ): Promise<TicketMessageEntity> => {
        const fileName = `audio${new Date().getTime()}.wav`;
        const mimeType = "audio/wav";
        const blob = await this._urlToBlobUseCase.call(audioRecorder.audioBlob!);
        const urlDownload = audioRecorder.audioBlob!;
        const file = new File([blob], fileName, { type: mimeType });
        const base64 = await fileToBase64(file);

        return new TicketMessageEntity({
            deleted: false,
            idCompany: getIdCompanyByLocalStorage(),
            idTicket: ticket.id,
            id: generateUUIDV4(),
            sender,
            senderId: sender.id,
            type: MessageChatType.audioMessage,
            blobURL: audioRecorder.audioBlob,
            audioMessage: new AudioMessageEntity({
                buffer: undefined,
                length: file.size,
                mimetype: mimeType,
                ptt: true,
                seconds: audioRecorder.duration
            }),
            uploadOptions: new UploadOptionsEntity({
                file: new FileMessageEntity({
                    base64Url: base64,
                    id: generateUUIDV4(),
                    file,
                    description: '',
                    blobURL: urlDownload,
                    isPrivate: message.isPrivate,
                }),
                progress: 0,
            }),
            createdAtLocal: new Date().toISOString(),
            isPrivate: message.isPrivate,
            fromMe: true,
            status: MessageStatus.PENDING,
            reply: message.reply
        })
    }

    @action
    prepareToSend = (body: BodyMessage, index?: number): TicketMessageEntity => {
        const { ticket, sender, message } = body;
        const senderId = body.sender.id;
        const id = message.id ?? generateUUIDV4();
        const hasSendId = !!this.currentChatMessages.data.find(message => message.id === id);
        const eligibleId = hasSendId ? generateUUIDV4() : id;


        const received: Record<string, boolean> = {};
        const readers: Record<string, boolean> = {};

        Object.keys(ticket.users ?? {}).forEach(userId => {
            received[userId] = userId === senderId;
            readers[userId] = userId === senderId;
        });
        return message.copyWith({
            id: eligibleId,
            idCompany: getIdCompanyByLocalStorage(),
            idTicket: ticket.id,
            ticket: ticket,
            senderId: sender.id,
            users: ticket.users,
            sender: sender,
            received,
            readers,
            createdAtLocal: getNowSumIndex(index ?? 0),
            //AQUI
            updatedAt: new Date().toISOString()
        });
    }

    @action
    async retrySendMessage(
        message: TicketMessageEntity,
        currentTicket: TicketEntity,
        userLogged: UserEntity
    ): Promise<void> {
        if (message.statusError === StatusMessageError.sendMessageError) {
            await this.resendMessage(message, currentTicket);
            return;
        }
        const messageRetry = message.copyWith({
            status: MessageStatus.PENDING,
            statusError: null
        });
        this.updateMessage(currentTicket, [messageRetry], undefined, undefined, undefined, undefined, undefined, true);
        await this.sendMessage(new BodyMessage({
            message: messageRetry,
            ticket: currentTicket,
            sender: userLogged
        }));
    }

    @action
    async resendMessage(
        message: TicketMessageEntity,
        currentTicket: TicketEntity
    ): Promise<void> {
        try {
            const messageResending = message.copyWith({
                status: MessageStatus.PENDING,
                statusError: null
            });
            this.updateMessage(currentTicket, [messageResending], undefined, undefined, undefined, undefined, undefined, true);
            const resentMessage = await this._resendMessageUseCase.call(message.id);
            this.updateMessage(currentTicket, [resentMessage], true, false);
        } catch (err: any) {
            const messageError = message.copyWith({
                status: MessageStatus.ERROR
            });
            this.updateMessage(currentTicket, [messageError], undefined, undefined, undefined, undefined, undefined, true);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    @action
    pushMemoryMessages(messages: TicketMessageEntity[], currentTicket: TicketEntity, userLogged: UserEntity) {
        for (let i = 0; i < messages.length; i++) {
            messages[i] = messages[i].copyWith({
                ...this.prepareToSend(new BodyMessage({
                    message: messages[i],
                    ticket: currentTicket,
                    sender: userLogged,
                }), i)
            });
        }
        this.updateMessage(currentTicket, messages, true, false);
        this.handleUpdateTicket(currentTicket.copyWith({
            lastMessage: messages[messages.length - 1],
        }));
    }

    @action
    async handleSendMessages(
        messages: TicketMessageEntity[],
        currentTicket: TicketEntity,
        userLogged: UserEntity,
        waitForSend = false
    ): Promise<void> {
        this.pushMemoryMessages(messages, currentTicket, userLogged);
        for (let index = 0; index < messages.length; index++) {
            if (currentTicket.forcePrivateComment(userLogged)) {
                messages[index].isPrivate = true;
            }
            const body = new BodyMessage({
                message: messages[index],
                ticket: currentTicket!,
                sender: userLogged
            });
            if (waitForSend) {
                await this.sendMessage(body);
            } else {
                this.sendMessage(body);
            }
        }
    };

    @action
    dispose = (): void => {
        this.subscriptionChats?.unsubscribe();
        this.subscriptionConnection?.unsubscribe();
        this.subscriptionUploadFile?.unsubscribe();
    }

    @action
    removeDuplicateMessages(messages: TicketMessageEntity[]): TicketMessageEntity[] {
        return BaseMessageEntity.removeDuplicateMessages(messages);
    }

    @action
    setReadByUsers = (users: UserEntity[]) => {
        this.readByUsers = users;
    }

    @action
    setReceivedByUsers = (users: UserEntity[]) => {
        this.receivedByUsers = users;
    }

    @action
    getUsersFromCache = async (message: ChatMessageEntity): Promise<void> => {
        this.receivedByUsers = [];
        this.readByUsers = [];
        if (message.readers) {
            const eligibleUsers = Object.keys(message.readers).filter(userId => message!.readers![userId] === true);
            const result = await this._getUsersFromCacheUseCase.call(eligibleUsers);
            this.setReadByUsers(result);
        }

        if (message.received) {
            const eligibleUsers = Object.keys(message.received).filter(userId => message!.received![userId] === true);
            const result = await this._getUsersFromCacheUseCase.call(eligibleUsers);
            this.setReceivedByUsers(result);
        }
    }

    @action
    handleDetailMessage = async (message: ChatMessageEntity): Promise<void> => {
        try {
            const remoteMessage = await this.getMessageById(message.id);
            await this.getUsersFromCache(remoteMessage);
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    getMessageById = async (id: string): Promise<TicketMessageEntity> => {
        return new Promise<TicketMessageEntity>(async (resolve, reject) => {
            try {
                const result = await this._getMessageByIdUseCase.call(id);
                return resolve(result);
            } catch (err: any) {
                return reject(err);
            }
        });
    }

    private getIsReconnect(): boolean {
        return localStorage.getItem(ConstantsKeys.isReconnectSubscription) === 'true';
    }

    private getTargetTicket(tickets: TicketEntity[]): TicketEntity | null {
        if (!this.currentChat?.id) {
            return null;
        }
        return tickets.find((ticket: TicketEntity) => ticket.id === this.currentChat?.id) ?? null;
    }

}
